import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import CustomerCard from '../components/CustomerCard';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Fade,
  TextField,
  InputAdornment,
  Tooltip,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress

} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneCallback as PhoneCallbackIcon,
  PhoneInTalk as PhoneInTalkIcon,
  PhoneMissed as PhoneMissedIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  CallEnd as CallEndIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SignalCellular4Bar as SignalIcon,
  PauseCircle as PauseCircleIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  ExitToApp as ExitToAppIcon,
  Home as HomeIcon,
  Call as CallIcon,
  TransferWithinAStation as TransferIcon,
  Mic as MicIconSolid,
  MicOff as MicOffIconSolid,
  FiberManualRecord as RecordIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Queue as QueueIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import freePBXWebRTCService from '../services/freePBXWebRTCService';
import { Invitation } from 'sip.js';

import { useSipNumber } from '../contexts/SipNumberContext';
import { useQueue } from '../contexts/QueueContext';
import { useUser } from '../contexts/UserContext';
import { useAgentStatus } from '../contexts/AgentStatusContext';
import { useQueueRouting } from '../contexts/QueueRoutingContext';
import { useCallRecording } from '../contexts/CallRecordingContext';
import authService from '../services/authService';
import api, { callAPI, reportsAPI, messagesAPI, dialerAPI } from '../services/api';
import { API_URL, SOCKET_URL } from '../config';
import './AgentPanel.css';

const AgentPanel: React.FC = () => {
  const [agentStatus, setAgentStatus] = useState('online');
  const [statusDuration, setStatusDuration] = useState('00:00:00');
  const [statusStartTime, setStatusStartTime] = useState<Date | null>(null);
  const [selectedQueue, setSelectedQueue] = useState<string>('');

  // ✨ Multi-line State Management
  interface LineState {
    isActive: boolean;
    phoneNumber: string; // Input field value
    currentCallPhoneNumber: string; // Active call number
    callDuration: number;
    callId: string | null;
    sipCallId?: string | null; // ✨ SIP Session ID
    isMuted: boolean;
    isHold: boolean;
  }

  const [activeLine, setActiveLine] = useState<1 | 2>(1);
  const [lines, setLines] = useState<{ 1: LineState; 2: LineState }>({
    1: { isActive: false, phoneNumber: '', currentCallPhoneNumber: '', callDuration: 0, callId: null, sipCallId: null, isMuted: false, isHold: false },
    2: { isActive: false, phoneNumber: '', currentCallPhoneNumber: '', callDuration: 0, callId: null, sipCallId: null, isMuted: false, isHold: false }
  });

  // Helper to get current line state
  const currentLine = lines[activeLine];

  // Helper to update specific line
  const updateLine = (lineNum: 1 | 2, updates: Partial<LineState>) => {
    setLines(prev => ({
      ...prev,
      [lineNum]: { ...prev[lineNum], ...updates }
    }));
  };

  // Helper to update active line
  const updateActiveLine = (updates: Partial<LineState>) => {
    updateLine(activeLine, updates);
  };

  // Legacy state wrappers for compatibility
  const phoneNumber = currentLine.phoneNumber;
  const setPhoneNumber = (value: string) => updateActiveLine({ phoneNumber: value });
  
  const isCallActive = currentLine.isActive;
  const setIsCallActive = (value: boolean | ((prev: boolean) => boolean)) => {
    if (typeof value === 'function') {
      updateActiveLine({ isActive: value(currentLine.isActive) });
    } else {
      updateActiveLine({ isActive: value });
    }
  };
  
  const callDuration = currentLine.callDuration;
  const setCallDuration = (value: number | ((prev: number) => number)) => {
    if (typeof value === 'function') {
      updateActiveLine({ callDuration: value(currentLine.callDuration) });
    } else {
      updateActiveLine({ callDuration: value });
    }
  };

  const currentCallPhoneNumber = currentLine.currentCallPhoneNumber;
  const setCurrentCallPhoneNumber = (value: string) => updateActiveLine({ currentCallPhoneNumber: value });
  
  const isMuted = currentLine.isMuted;
  const setIsMuted = (value: boolean) => updateActiveLine({ isMuted: value });
  
  const isOnHold = currentLine.isHold;
  const setIsOnHold = (value: boolean) => updateActiveLine({ isHold: value });

  const currentCallId = currentLine.callId;
  const setCurrentCallId = (value: string | null) => updateActiveLine({ callId: value });

  // const [phoneNumber, setPhoneNumber] = useState('');
  // const [isCallActive, setIsCallActive] = useState(false);
  // const [callDuration, setCallDuration] = useState(0);
  // const [currentCallPhoneNumber, setCurrentCallPhoneNumber] = useState<string>('');
  // const [isMuted, setIsMuted] = useState(false);
  // const [isOnHold, setIsOnHold] = useState(false);
  // const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  
  const [socket, setSocket] = useState<any>(null);
  const [incomingCall, setIncomingCall] = useState<{ username: string; callId: string; callerNumber: string; queueName?: string; queueId?: string; jsep?: any; sessionId?: number; handleId?: number; queueData?: any } | null>(null);
  const [showCallDialog, setShowCallDialog] = useState(false);
  const [ringtoneAudio, setRingtoneAudio] = useState<HTMLAudioElement | null>(null);
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
  const isCallActiveRef = useRef(false);
  const callAnsweredRef = useRef(false); // ✨ Çağrı gerçekten cevaplanıp konuşuldu mu?
  const currentCallIdRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<number | null>(null); // Çağrı başlangıç zamanı
  const [softphoneStatus, setSoftphoneStatus] = useState<'bağlı' | 'bağlı değil'>('bağlı değil');
  const [availableStatuses, setAvailableStatuses] = useState<any[]>([]);
  const [currentStatusInfo, setCurrentStatusInfo] = useState<any>(null);
  const [showQueueDropdown, setShowQueueDropdown] = useState(false);
  const [line1Status, setLine1Status] = useState('boş');
  const [line2Status, setLine2Status] = useState('boş');
  const [waitingCalls, setWaitingCalls] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('Aktif Çağrı');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isBrowserRecording, setIsBrowserRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showCustomerCard, setShowCustomerCard] = useState(false);
  const [isPendingFinishCode, setIsPendingFinishCode] = useState(false); // Finish code bekleyen çağrı var mı
  // ✨ External customer data and queue CRM config
  const [currentCustomerCardData, setCurrentCustomerCardData] = useState<any>(null);
  const [currentQueueData, setCurrentQueueData] = useState<any>(null);
  // ✨ Statü yönetimi için önceki statüyü sakla
  const [previousStatusName, setPreviousStatusName] = useState<string | null>(null);
  const [previousStatusStartTime, setPreviousStatusStartTime] = useState<Date | null>(null);
  
  // ✨ Transfer/Multi-line state
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [activeChannels, setActiveChannels] = useState<Array<{channel: string, number: string, state: string}>>([]);
  // ✨ Agent bilgileri
  const [agentExtension, setAgentExtension] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  
  // ✨ Dialpad state
  const [showDialpad, setShowDialpad] = useState(false);
  const [dialpadInput, setDialpadInput] = useState('');

  // ✨ Dialer call state
  interface DialerCallInfo {
    dialerCallId: string;
    campaignId: string;
    campaignName: string;
    contactId: string;
    phoneNumber: string;
    contactName?: string;
    contactCompany?: string;
    customFields?: Record<string, any>;
    attemptNumber: number;
    listId?: string;
  }
  const [dialerCall, setDialerCall] = useState<DialerCallInfo | null>(null);
  const [showDispositionDialog, setShowDispositionDialog] = useState(false);
  const [selectedDisposition, setSelectedDisposition] = useState<string>('');
  const [dispositionNotes, setDispositionNotes] = useState('');
  const [savingDisposition, setSavingDisposition] = useState(false);

  // Navigation
  const navigate = useNavigate();
  
  // Context hooks
  const sipNumberContext = useSipNumber();
  const queueContext = useQueue();
  const userContext = useUser();
  const agentStatusContext = useAgentStatus();
  const queueRoutingContext = useQueueRouting();
  const callRecordingContext = useCallRecording();

  // Get current user
  const currentUser = authService.getCurrentUser();
  
  // Previous calls state
  const [previousCalls, setPreviousCalls] = useState<any[]>([]);
  const [loadingPreviousCalls, setLoadingPreviousCalls] = useState(true);
  const [previousCallsSearch, setPreviousCallsSearch] = useState('');
  
  // Performance data state
  const [performanceData, setPerformanceData] = useState({
    totalCalls: 0,
    answeredCalls: 0,
    missedCalls: 0,
    avgCallDuration: '00:00:00',
    avgWaitTime: '00:00:00',
    satisfactionRate: 0,
    // New call statistics
    incomingCalls: 0,
    outgoingCalls: 0,
    totalCallDuration: '00:00:00',
    incomingCallDuration: '00:00:00',
    outgoingCallDuration: '00:00:00',
    avgTotalCallDuration: '00:00:00',
    avgIncomingCallDuration: '00:00:00',
    avgOutgoingCallDuration: '00:00:00'
  });
  const [loadingPerformance, setLoadingPerformance] = useState(true);

  // Agent status history state
  const [statusHistory, setStatusHistory] = useState<any[]>([]);

  // Agent status durations state
  const [statusDurations, setStatusDurations] = useState<any[]>([]);
  
  // ✨ Messages state
  const [users, setUsers] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [showUserSelectDialog, setShowUserSelectDialog] = useState(false);
  
  // Load initial data
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.log('No token found, redirecting to login');
      navigate('/login');
      return;
    }
    
    // Check if token is valid by trying to decode it
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      if (payload.exp < now) {
        console.log('Token expired, clearing and redirecting to login');
        localStorage.clear();
        navigate('/login');
        return;
      }
    } catch (error) {
      console.log('Invalid token, clearing and redirecting to login');
      localStorage.clear();
      navigate('/login');
      return;
    }
    
    console.log('🔄 AgentPanel: Loading initial data...');
    console.log('🔍 Current user:', currentUser);
    console.log('🔍 User queueIds:', currentUser?.queueIds);
    
    // Personelika configuration
    (window as any).listenOnlineEvent = true;
    (window as any).number_masking = false;
    (window as any).enable_reviews_tab = true;
    (window as any).enable_chat_system = false;
    (window as any).chat_enable_reviews_tab = false;
    (window as any).display_iyzico_app = true;
    (window as any).allow_agent_reserve_status = false;
    (window as any).start_parking_chat_allow = false;
    (window as any).chat_server = "chatserver.alo-tech.com";
    (window as any).tenantname = "personelika.alo-tech.com";
    (window as any).allow_shortcuts = false;
    (window as any).lock_screen_for_agent = false;
    (window as any).lock_screen = false;
    (window as any).deactivate_getmyqueueactivity = false;
    (window as any).allow_pause_recording = true; // Otomatik kayıt aktif
    (window as any).allow_manual_popup = false;
    (window as any).enable_videocall_system = false;
    (window as any).enable_ai_platform = false;
    (window as any).tenant_user_master_admin = false;
    (window as any).lang = "tr";
    (window as any).time_zone = 'Europe/Istanbul';
    (window as any).is_acl_active = true;
    (window as any).enable_email = false;
    (window as any)._user_role = "agent";
    (window as any)._tenantname = "personelika.alo-tech.com";
    (window as any)._name = currentUser?.firstName + " " + currentUser?.lastName || "Melek Korkmaz";
    (window as any)._email = currentUser?.email || "melek.korkmaz@personelika.com";
    (window as any).enable_symbl_agent_assist = false;
    (window as any).force_tagging_for_agent = false;
    (window as any).allow_single_tag_selection_for_agent = false;
    (window as any).suspend_pages = [];
    (window as any).show_banner_for_unpaid_invoice = [];
    
    // Fetch queues, SIP numbers, and agent statuses
    queueContext.fetchQueues();
    sipNumberContext.fetchSipNumbers();
    agentStatusContext.fetchStatuses();
    
    // ✨ Load status start time from backend to continue duration after page refresh
    const loadStatusStartTime = async () => {
      try {
        const result = await authService.getCurrentUserFromAPI();
        if (result.success && result.user) {
          const user = result.user as any;
          if (user.statusStartTime) {
            const startTime = new Date(user.statusStartTime);
            setStatusStartTime(startTime);
            
            // Calculate elapsed time
            const now = new Date();
            const elapsedMs = now.getTime() - startTime.getTime();
            const elapsedSeconds = Math.floor(elapsedMs / 1000);
            const hours = Math.floor(elapsedSeconds / 3600);
            const minutes = Math.floor((elapsedSeconds % 3600) / 60);
            const seconds = elapsedSeconds % 60;
            const formattedDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            setStatusDuration(formattedDuration);
            console.log('✅ Status duration restored from backend:', formattedDuration, 'Start time:', startTime);
          } else {
            console.log('⚠️ No statusStartTime found for user');
            setStatusStartTime(new Date());
            setStatusDuration('00:00:00');
          }
        }
      } catch (error) {
        console.error('❌ Error loading status start time:', error);
        setStatusStartTime(new Date());
        setStatusDuration('00:00:00');
      }
    };
    
    loadStatusStartTime();
  }, []);
    
  // Load previous calls data
  useEffect(() => {
    const loadPreviousCalls = async () => {
      if (activeTab === 'Önceki Çağrılar') {
        setLoadingPreviousCalls(true);
        try {
          // Get calls for the current agent
          const response = await callAPI.getAll({
            agentId: currentUser?._id,
            limit: 50,
            sortBy: 'startTime',
            sortOrder: 'desc'
          });

          console.log('📞 Previous calls response:', response.data);

          if (response.data?.success && response.data?.data) {
            setPreviousCalls(response.data.data);
          }
        } catch (error) {
          console.error('❌ Error loading previous calls:', error);
        } finally {
          setLoadingPreviousCalls(false);
        }
      }
    };

    loadPreviousCalls();
  }, [activeTab, currentUser?._id]);
    
  // Load performance data
  useEffect(() => {
    const loadPerformanceData = async () => {
      console.log('🔄 loadPerformanceData called, activeTab:', activeTab, 'currentUser._id:', currentUser?._id);
      
      if (activeTab === 'Performans') {
        if (!currentUser?._id) {
          console.error('❌ currentUser._id is undefined, cannot load performance data');
          return;
        }
        
        setLoadingPerformance(true);
        try {
          console.log('📡 Fetching /agent-stats/today/' + currentUser._id);
          // Get agent performance data from agent-stats (same as SupervisorDashboard)
          const statsResponse = await api.get(`/agent-stats/today/${currentUser._id}`);
          const stats = statsResponse?.data?.data || {};
          
          // Format duration helper
          const formatDuration = (seconds: number) => {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          };

          // Get agent status durations for today only
          const todayDate = new Date();
          const startOfDay = new Date(todayDate.setHours(0, 0, 0, 0));
          const endOfDay = new Date(todayDate.setHours(23, 59, 59, 999));

          const statusDurationsResponse = await reportsAPI.getAgentStatusDurations(
            currentUser?._id,
            startOfDay.toISOString(),
            endOfDay.toISOString()
          );

          console.log('📊 Agent stats response:', stats);
          console.log('📊 Status durations response:', statusDurationsResponse.data);

          // Set performance data from agent-stats (same format as SupervisorDashboard)
          setPerformanceData({
            totalCalls: stats.totalCalls || 0,
            answeredCalls: stats.answeredCalls || 0,
            missedCalls: stats.missedCalls || 0,
            avgCallDuration: formatDuration(stats.avgCallDuration || 0),
            avgWaitTime: '00:00:00', // TODO: add waitTime calculation
            satisfactionRate: stats.totalCalls > 0 ? Math.round((stats.answeredCalls / stats.totalCalls) * 100) : 0,
            // Call statistics
            incomingCalls: stats.incomingCalls || 0,
            outgoingCalls: stats.outgoingCalls || 0,
            totalCallDuration: formatDuration(stats.totalDuration || 0),
            incomingCallDuration: formatDuration(stats.incomingDuration || 0),
            outgoingCallDuration: formatDuration(stats.outgoingDuration || 0),
            avgTotalCallDuration: formatDuration(stats.avgCallDuration || 0),
            avgIncomingCallDuration: formatDuration(stats.avgIncomingDuration || 0),
            avgOutgoingCallDuration: formatDuration(stats.avgOutgoingDuration || 0)
          });

          console.log('✅ Performance data set:', {
            totalCalls: stats.totalCalls,
            answeredCalls: stats.answeredCalls,
            avgCallDuration: stats.avgCallDuration
          });

          // Set status durations
          if (statusDurationsResponse.data?.success && statusDurationsResponse.data?.statusDurations) {
            setStatusDurations(statusDurationsResponse.data.statusDurations);
          }
        } catch (error: any) {
          console.error('❌ Error loading performance data:', error);
          // Check if it's an auth error
          if (error?.response?.status === 401 || error?.message?.includes('token')) {
            console.log('🔒 Auth error detected, logging out...');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            navigate('/login');
          }
        } finally {
          setLoadingPerformance(false);
        }
      }
    };
      
    loadPerformanceData();
  }, [activeTab, currentUser?._id]);
    
  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showProfileMenu) {
        const profileMenu = document.querySelector('.profile-menu');
        const profileButton = document.querySelector('.sidebar-profile');
        
        if (profileMenu && !profileMenu.contains(event.target as Node) && 
            profileButton && !profileButton.contains(event.target as Node)) {
          setShowProfileMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  // FreePBX WebRTC initialization
  useEffect(() => {
    console.log('🔄 FreePBX useEffect running, currentUser._id:', currentUser?._id);
    
    const initializeFreePBX = async () => {
      try {
        console.log('🎯 Initializing FreePBX WebRTC...');

        // 🎤 Mikrofon iznini hemen iste (Sayfa yüklendiğinde)
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('✅ Microphone permission granted on load');
        } catch (err) {
          console.error('❌ Microphone permission denied:', err);
          setSnackbarMessage('Lütfen mikrofon izni verin!');
          setSnackbarOpen(true);
        }
        
        // Backend'den WebRTC credentials al
        const response = await fetch(`${API_URL}/agent/webrtc-credentials`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        
        const data = await response.json();
        
        if (!data.hasExtension) {
          console.warn('⚠️ Agent has no SIP extension assigned');
          setSoftphoneStatus('bağlı değil');
          return;
        }
        
        console.log('📞 Connecting to FreePBX:', data.server);
        console.log('📋 Agent credentials:', { agentId: data.agentId, extension: data.extension });
        
        // Agent bilgilerini state'e kaydet
        setAgentExtension(data.extension);
        setAgentId(data.agentId);
        
        // 🆕 Global scope ve localStorage'a kaydet (Socket listener erişebilsin diye)
        (window as any)._agentExtension = data.extension;
        localStorage.setItem('agentExtension', data.extension);
        
        // FreePBX WebRTC servisine bağlan
        await freePBXWebRTCService.connect({
          extension: data.extension,
          password: data.password,
          server: data.server,
          realm: data.realm,
          displayName: data.displayName
        });
        
        // Debug için global scope'a ekle
        (window as any).freePBXWebRTCService = freePBXWebRTCService;
        console.log('🔧 freePBXWebRTCService added to window for debugging');
        
        // Event listeners
        freePBXWebRTCService.onRegistered = () => {
          console.log('✅ FreePBX registered');
          setSoftphoneStatus('bağlı');
          
          // Backend'e agent bağlandı bildir
          fetch(`${API_URL}/agent/connect`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            },
            body: JSON.stringify({ 
              agentId: data.agentId,
              extension: data.extension,
              queueIds: currentUser?.queueIds,
              socketId: socket?.id 
            })
          });
        };
        
        freePBXWebRTCService.onUnregistered = () => {
          console.log('❌ FreePBX unregistered');
          setSoftphoneStatus('bağlı değil');
        };
        
        freePBXWebRTCService.onReconnecting = (attempt: number, maxAttempts: number) => {
          console.log(`🔄 Reconnecting... (${attempt}/${maxAttempts})`);
          setSoftphoneStatus('bağlı değil');
          setSnackbarMessage(`SIP bağlantısı kesildi, yeniden bağlanılıyor... (${attempt}/${maxAttempts})`);
          setSnackbarOpen(true);
        };
        
        freePBXWebRTCService.onIncomingCall = (callerNumber: string, calledNumber: string, invitation: any) => {
          console.log('📞 onIncomingCall triggered!');
          console.log('📞 Incoming call from:', callerNumber, 'to:', calledNumber);
          
          // 🚫 Eğer finish code bekliyorsak yeni çağrıyı reddet
          if (isPendingFinishCode) {
            console.log('⛔ Rejecting incoming call - finish code pending');
            try {
              invitation.reject();
            } catch (e) {
              console.error('Error rejecting call:', e);
            }
            return;
          }
          
          // 🚫 Eğer aktif çağrı varsa yeni çağrıyı reddet
          if (isCallActiveRef.current) {
            console.log('⛔ Rejecting incoming call - call already active');
            try {
              invitation.reject();
            } catch (e) {
              console.error('Error rejecting call:', e);
            }
            return;
          }
          
          // 1. Display Name'i al (Genellikle "Queue Name" veya "CID:QueueName" formatındadır)
          let displayName = invitation.remoteIdentity?.displayName || '';
          console.log('👤 Caller Display Name:', displayName);

          // 2. Headerlardan Kuyruk Bilgisi Ara
          let queueName = '';
          let sipNumber = calledNumber;

          try {
            const headers = invitation.request.headers;
            console.log('📜 SIP Headers:', headers);
            
            // X-Queue veya X-Queue-Name headerlarını kontrol et
            if (headers['X-Queue']) queueName = headers['X-Queue'][0].raw;
            else if (headers['X-Queue-Name']) queueName = headers['X-Queue-Name'][0].raw;
            
            // Diversion header'dan DID bul
            if (headers['Diversion']) {
              const match = headers['Diversion'][0].raw.match(/sip:(\d+)@/);
              if (match) sipNumber = match[1];
            }
          } catch (e) {
            console.warn('⚠️ Error parsing headers:', e);
          }

          // 3. Kuyruk ismini belirle
          // Eğer headerlardan gelmediyse, Display Name'den çıkarmaya çalış
          if (!queueName && displayName) {
             // Örnek: "Satis <0555...>" -> "Satis"
             // Örnek: "CID:Satis" -> "Satis"
             // Sadece harf ve boşluk içeren kısmı al (numaraları temizle)
             const cleanName = displayName.replace(/<.*>/, '').replace('CID:', '').trim();
             if (cleanName && cleanName !== callerNumber) {
               queueName = cleanName;
             }
          }

          // Eğer hala yoksa ve DID farklıysa DID'yi kullan
          if (!queueName && sipNumber !== calledNumber) {
            queueName = `Hat: ${sipNumber}`;
          }
          
          // Eğer hiçbiri yoksa varsayılan
          if (!queueName) {
            queueName = 'Gelen Çağrı';
          }

          console.log('🏷️ Final Queue Name:', queueName);
          
          // 🎯 Hemen geçici ID ile başla - backend ID'sini arka planda al
          const tempCallId = `incoming-${Date.now()}`;
          
          // Zil sesini çal
          try {
            if (ringtoneAudioRef.current) {
              ringtoneAudioRef.current.pause();
              ringtoneAudioRef.current.currentTime = 0;
            }
            
            const audio = new Audio('/sounds/incoming-call.mp3');
            audio.loop = true;
            audio.volume = 0.7;
            audio.play().catch(err => console.warn('⚠️ Could not play ringtone:', err));
            ringtoneAudioRef.current = audio;
            setRingtoneAudio(audio);
          } catch (error) {
            console.error('❌ Error playing ringtone:', error);
          }
          
          // Gelen çağrı dialog'unu göster - HEMEN
          setIncomingCall({
            username: displayName || callerNumber, // İsim varsa ismi göster
            callId: tempCallId,
            callerNumber: callerNumber,
            queueName: queueName
          });
          setCurrentCallPhoneNumber(callerNumber);
          setShowCallDialog(true);
          
          // 🆕 Geçici ID'yi set et
          setCurrentCallId(tempCallId);
          currentCallIdRef.current = tempCallId;
          
          // ✨ Queue isminden queue bilgilerini al ve CRM config'i set et
          if (queueName && queueName !== 'Gelen Çağrı') {
            (async () => {
              try {
                // Tüm queue'ları al ve isimle eşleştir
                const queuesResponse = await fetch(`${API_URL}/queues`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                  }
                });
                if (queuesResponse.ok) {
                  const queuesData = await queuesResponse.json();
                  const queues = queuesData.data || queuesData.queues || queuesData;
                  console.log('📋 Queues API response:', { hasData: !!queuesData.data, hasQueues: !!queuesData.queues, queueCount: Array.isArray(queues) ? queues.length : 'not-array' });
                  
                  let matchedQueue: any = null;
                  let displayQueueName = queueName;
                  
                  // 🆕 "prefix:numara" formatını kontrol et (örn: "800:5551234")
                  const prefixMatch = queueName.match(/^(\d+):(\d+)$/);
                  if (prefixMatch) {
                    const prefix = prefixMatch[1];
                    const callerNum = prefixMatch[2];
                    console.log('📋 Detected prefix:number format - prefix:', prefix, 'number:', callerNum);
                    
                    // outboundPrefix ile eşleşen queue'yu bul
                    matchedQueue = queues.find((q: any) => q.outboundPrefix === prefix);
                    if (matchedQueue) {
                      displayQueueName = matchedQueue.name;
                      console.log('📋 Matched queue by outboundPrefix:', matchedQueue.name);
                      
                      // queueName'i güncelle
                      setIncomingCall(prev => prev ? { ...prev, queueName: displayQueueName } : null);
                    }
                  }
                  
                  // Eğer prefix ile bulamadıysak normal isim eşleştirme yap
                  if (!matchedQueue) {
                    const normalizedQueueName = queueName.toLowerCase().replace(/[^a-z0-9]/g, '');
                    matchedQueue = queues.find((q: any) => {
                      const qName = (q.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                      return qName === normalizedQueueName || (q.name || '').includes(queueName) || queueName.includes(q.name || '');
                    });
                  }
                  
                  if (matchedQueue) {
                    console.log('📋 Matched queue for incoming call:', matchedQueue.name);
                    
                    // CRM config'i hazırla
                    const queueCrmConfig = (matchedQueue.customerCardDatasetListUrl || matchedQueue.customerCardApiUrl) ? {
                      customerCardDatasetListUrl: matchedQueue.customerCardDatasetListUrl,
                      customerCardDatasetSchemaUrl: matchedQueue.customerCardDatasetSchemaUrl,
                      customerCardDatasetAddUrl: matchedQueue.customerCardDatasetAddUrl,
                      customerCardSelectedDatasetId: matchedQueue.customerCardSelectedDatasetId,
                      customerCardApiUrl: matchedQueue.customerCardApiUrl
                    } : null;
                    
                    // IncomingCall'a queueId ve queueData ekle
                    setIncomingCall(prev => prev ? { ...prev, queueId: matchedQueue._id, queueData: queueCrmConfig } : null);
                    
                    // CRM config'i set et
                    if (queueCrmConfig) {
                      console.log('📋 Setting CRM config from matched queue:', queueCrmConfig);
                      setCurrentQueueData(queueCrmConfig);
                      
                      // 🆕 Eğer customerCardApiUrl varsa, gelen çağrının numarasından müşteri verisini çek
                      if (queueCrmConfig.customerCardApiUrl && callerNumber) {
                        console.log('📞 Fetching customer data from CRM API for incoming call:', callerNumber);
                        try {
                          const crmResponse = await fetch(`${API_URL}/crm-proxy/customer?phone=${encodeURIComponent(callerNumber)}&apiUrl=${encodeURIComponent(queueCrmConfig.customerCardApiUrl)}`, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                            }
                          });
                          
                          if (crmResponse.ok) {
                            const crmData = await crmResponse.json();
                            if (crmData.success && crmData.data) {
                              console.log('✅ CRM customer data received for incoming call:', crmData.data);
                              setCurrentCustomerCardData(crmData.data);
                            } else {
                              console.log('ℹ️ No customer data found in CRM for:', callerNumber);
                              setCurrentCustomerCardData(null);
                            }
                          } else {
                            console.warn('⚠️ CRM API returned error:', crmResponse.status);
                          }
                        } catch (crmErr) {
                          console.warn('⚠️ Could not fetch customer from CRM:', crmErr);
                        }
                      }
                    }
                  }
                }
              } catch (err) {
                console.warn('⚠️ Could not fetch queue by name:', err);
              }
            })();
          }
          
          // Invitation'ı sakla (cevap vermek için)
          (window as any)._currentInvitation = invitation;
          
          // 🔄 Arka planda backend'den gerçek ID'yi al ve güncelle
          (async () => {
            try {
              const response = await fetch(`${API_URL}/calls/active`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                }
              });
              
              if (response.ok) {
                const data = await response.json();
                // Bu agent için ringing durumunda olan çağrıyı bul
                const activeCall = data.calls?.find((call: any) => {
                  const callAgentId = typeof call.agentId === 'object' ? call.agentId._id : call.agentId;
                  return callAgentId === currentUser?._id && 
                         (call.status === 'ringing' || call.status === 'waiting') &&
                         call.callerNumber === callerNumber;
                });
                
                if (activeCall && activeCall._id) {
                  console.log('✅ Found backend call ID, updating:', activeCall._id);
                  // Sadece henüz cevaplamadıysak ID'yi güncelle
                  if (currentCallIdRef.current === tempCallId) {
                    setCurrentCallId(activeCall._id);
                    currentCallIdRef.current = activeCall._id;
                    
                    // IncomingCall state'ini de güncelle
                    setIncomingCall(prev => prev ? { ...prev, callId: activeCall._id } : null);
                    
                    // ✨ Queue CRM config'ini al
                    if (activeCall.queueId) {
                      const queueId = typeof activeCall.queueId === 'object' ? activeCall.queueId._id : activeCall.queueId;
                      try {
                        const queueResponse = await fetch(`${API_URL}/queues/${queueId}`, {
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                          }
                        });
                        if (queueResponse.ok) {
                          const queueDetails = await queueResponse.json();
                          console.log('📋 Queue CRM config fetched:', queueDetails);
                          setCurrentQueueData({
                            customerCardDatasetListUrl: queueDetails.customerCardDatasetListUrl,
                            customerCardDatasetSchemaUrl: queueDetails.customerCardDatasetSchemaUrl,
                            customerCardDatasetAddUrl: queueDetails.customerCardDatasetAddUrl,
                            customerCardSelectedDatasetId: queueDetails.customerCardSelectedDatasetId,
                            customerCardApiUrl: queueDetails.customerCardApiUrl
                          });
                        }
                      } catch (qErr) {
                        console.warn('⚠️ Could not fetch queue CRM config:', qErr);
                      }
                    }
                  }
                }
              }
            } catch (error) {
              console.warn('⚠️ Could not fetch backend call ID (using temp ID):', error);
            }
          })();
        };
        
        freePBXWebRTCService.onCallEstablished = async (targetNumber?: string) => {
          console.log('✅ Call established', targetNumber);
          setIsCallActive(true);
          setCallDuration(0);
          
          // Karşı taraf açtı bildirimi (kısa ses)
          try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const beepFrequency = 1000; // 1000 Hz
            const beepDuration = 0.1; // 100ms
            
            // Tek beep sesi
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.value = beepFrequency;
            oscillator.type = 'sine';
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + beepDuration);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + beepDuration);
          } catch (e) {
            console.log('Could not play beep:', e);
          }
          
          // Backend'e çağrı answered bildir
          if (currentCallId) {
            try {
              console.log(`✅ Notifying backend: Call ${currentCallId} answered`);
              await fetch(`${API_URL}/calls/${currentCallId}/answered`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                },
                body: JSON.stringify({ agentId: currentUser?._id })
              });
              console.log(`📞 Backend notified: Call ${currentCallId} is active`);
            } catch (error) {
              console.error('❌ Failed to notify backend:', error);
            }
          }
        };
        
        freePBXWebRTCService.onCallEnded = () => {
          console.log('📴 Call ended (remote hangup or network issue)');
          const callIdToEnd = currentCallIdRef.current;
          
          // Kapanma sesi çal (dit dit dit)
          console.log('🔊 Playing hangup beep...');
          try {
            const playBeep = () => {
              const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
              
              // Gerçek telefon meşgul sesi: 2 beep, pause, 2 beep, pause, 2 beep
              const beepPattern = [0, 0.25]; // Beep zamanları
              
              beepPattern.forEach(time => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.value = 480; // Hz
                oscillator.type = 'sine';
                
                const startTime = audioContext.currentTime + time;
                gainNode.gain.setValueAtTime(0.25, startTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
                
                oscillator.start(startTime);
                oscillator.stop(startTime + 0.2);
              });
            };
            
            playBeep();
            console.log('✅ Hangup beep played');
          } catch (e) {
            console.error('❌ Could not play beep:', e);
          }
          
          // State'leri temizle
          setIsCallActive(false);
          isCallActiveRef.current = false;
          setCallDuration(0);
          // ✨ Telefon numarasını SAKLIYORUZ - müşteri kartı açık kalsın
          // setCurrentCallPhoneNumber(''); // ❌ Bunu kaldırdık
          // ⚠️ currentCallId'yi KORUYORUZ - finish code için gerekli
          // setCurrentCallId(null); ❌ Bunu kaldırdık
          // currentCallIdRef.current = null; ❌ Bunu kaldırdık
          callStartTimeRef.current = null;
          setIsMuted(false);
          setIsOnHold(false);
          setShowCallDialog(false);
          setIncomingCall(null);
          // ✨ Müşteri kartını açık tut
          setShowCustomerCard(true);
          
          // Zil sesini durdur
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
          }
          
          // Backend'e bildir - use captured callId
          if (callIdToEnd) {
            console.log(`📞 Notifying backend: Call ${callIdToEnd} ended`);
            fetch(`${API_URL}/agent/call/${callIdToEnd}/end`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              },
              body: JSON.stringify({
                agentId: agentId || currentUser?.userId,
                extension: agentExtension
              })
            }).catch(err => console.error('Error notifying backend:', err));
          }
        };
        
        freePBXWebRTCService.onCallFailed = (error: Error) => {
          console.error('❌ Call failed:', error);
          alert('Çağrı başarısız: ' + error.message);
        };
        
        console.log('✅ FreePBX WebRTC initialized');
        
      } catch (error) {
        console.error('❌ FreePBX initialization error:', error);
        setSoftphoneStatus('bağlı değil');
      }
    };
    
    let isInitialized = false;
    
    if (currentUser?._id && !isInitialized) {
      isInitialized = true;
      console.log('🚀 Starting FreePBX initialization for user:', currentUser._id);
      initializeFreePBX();
    }
    
    // Cleanup
    return () => {
      console.log('🧹 Cleaning up FreePBX connection...');
      freePBXWebRTCService.disconnect();
    };
  }, [currentUser?._id]);

  // Socket.IO connection
  useEffect(() => {
    const socketUrl = SOCKET_URL;
    const newSocket = io(socketUrl);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('🔌 Socket connected:', newSocket.id);
      if (currentUser && currentUser._id) {
        console.log(`🚪 Joining user room: user-${currentUser._id}`);
        newSocket.emit('join-user-room', currentUser._id);
        
        // ✨ CRITICAL: Agent için AudioBridge room oluştur
        console.log(`🏠 Requesting AudioBridge room for agent ${currentUser._id}`);
        newSocket.emit('agent-request-room', { agentId: currentUser._id });
        
        // ✨ Load messages data
        loadUsers();
        loadConversations();
        loadUnreadCount();
      }
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
    });

    newSocket.on('newCallInQueue', (data: { queueId: string; queueName: string; callId: string; waitingCalls: number }) => {
      console.log('📞 Yeni çağrı kuyruğa eklendi:', data);
      setWaitingCalls(prev => [...prev, data]);
    });

    newSocket.on('callAccepted', (data: { agentId: string; callId: string }) => {
      console.log('✅ Çağrı kabul edildi:', data);
      setWaitingCalls(prev => prev.filter(call => call.callId !== data.callId));
    });

    newSocket.on('call-incoming', (data: {
      callId: string;
      callerNumber: string;
      calledNumber: string;
      queueId: string;
      queueName?: string;
      sipNumber: string;
      timestamp: string;
      jsep?: any;
      sessionId?: number;
      handleId?: number;
      customerCardData?: any;
      queueData?: {
        customerCardDatasetListUrl?: string;
        customerCardDatasetSchemaUrl?: string;
        customerCardDatasetAddUrl?: string;
        customerCardSelectedDatasetId?: string;
      };
    }) => {
      console.log('🔔 Gelen çağrı:', data);
      console.log('🔍 Seçili kuyruk:', selectedQueue);
      console.log('🔍 Çağrının kuyruğu:', data.queueId);
      
      // Store external customer data and queue config
      if (data.customerCardData) {
        console.log('📋 External customer data received:', data.customerCardData);
        setCurrentCustomerCardData(data.customerCardData);
      }
      if (data.queueData) {
        console.log('⚙️ Queue CRM config received:', data.queueData);
        setCurrentQueueData(data.queueData);
      }
      
      // ✨ Play ringtone sound
      try {
        // Stop any existing ringtone first
        if (ringtoneAudioRef.current) {
          ringtoneAudioRef.current.pause();
          ringtoneAudioRef.current.currentTime = 0;
        }
        
        const audio = new Audio('/sounds/incoming-call.mp3');
        audio.loop = true;
        audio.volume = 0.7;
        audio.play().catch(err => {
          console.warn('⚠️ Could not play ringtone:', err);
        });
        ringtoneAudioRef.current = audio;
        setRingtoneAudio(audio);
      } catch (error) {
        console.error('❌ Error playing ringtone:', error);
      }
      
      console.log('✅ Çağrı dialog açılıyor');
      setIncomingCall({
        username: data.sipNumber,
        callId: data.callId,
        callerNumber: data.callerNumber,
        queueName: data.queueName || (queueContext.queues.find(q => q._id === data.queueId)?.name),
        queueData: data.queueData, // ✨ Socket'ten gelen CRM config
        jsep: data.jsep,
        sessionId: data.sessionId,
        handleId: data.handleId
      });
      setShowCallDialog(true);
    });

    // ✨ Backend'den gelen incoming-call event'ini dinle (MongoDB ID'sini almak için)
    newSocket.on('incoming-call', (data: {
      callId: string; // MongoDB _id
      callerNumber: string;
      calledNumber: string;
      agentExtension: string;
      queue?: { queueId: string; queueName: string };
      queueData?: {
        customerCardDatasetListUrl?: string;
        customerCardDatasetSchemaUrl?: string;
        customerCardDatasetAddUrl?: string;
        customerCardSelectedDatasetId?: string;
        customerCardApiUrl?: string;
      };
    }) => {
      console.log('📞 Backend incoming-call event received:', data);
      console.log('📋 Queue CRM config in incoming-call:', data.queueData);
      
      // ✨ Queue CRM config varsa hemen set et
      if (data.queueData && (data.queueData.customerCardDatasetListUrl || data.queueData.customerCardApiUrl)) {
        console.log('📋 Setting CRM config from backend incoming-call event');
        setCurrentQueueData(data.queueData);
      }
      
      // 🔍 agentExtension state'i bazen güncellenmemiş olabiliyor, localStorage'dan veya prop'tan kontrol et
      // Ancak burada en güvenlisi data.agentExtension ile bizim beklediğimiz extension'ı karşılaştırmak
      // Şimdilik sadece loglayalım, ama extension kontrolünü esnetelim
      
      const currentExtension = agentExtension || (window as any)._agentExtension;
      console.log('🔍 Current agentExtension (State/Window):', currentExtension);
      console.log('🔍 Data agentExtension:', data.agentExtension);
      
      // Eğer bu çağrı bizim extension'ımız içinse ve MongoDB ID varsa
      // NOT: agentExtension boş olsa bile, eğer kullanıcı login olduysa ve çağrı geldiyse muhtemelen onundur.
      // Ancak çoklu agent senaryosunda karışıklık olmaması için extension kontrolü önemli.
      // Fallback olarak: Eğer data.agentExtension bizim localStorage'daki extension ile eşleşiyorsa kabul et.
      
      const storedExtension = localStorage.getItem('agentExtension');
      const isMyCall = data.agentExtension === currentExtension || data.agentExtension === storedExtension;

      if (isMyCall && data.callId) {
        const currentId = currentCallIdRef.current;
        
        // Eğer şu an temp ID kullanıyorsak (incoming-XXXXX formatında) veya ID yoksa
        if (!currentId || currentId.startsWith('incoming-')) {
          console.log(`🔄 Updating call ID from "${currentId || 'none'}" to MongoDB "${data.callId}"`);
          setCurrentCallId(data.callId);
          currentCallIdRef.current = data.callId;
          
          // IncomingCall state'ini de güncelle
          setIncomingCall(prev => {
            if (prev) {
              return { 
                ...prev, 
                callId: data.callId,
                queueName: data.queue?.queueName || prev.queueName, // Backend kuyruk adı varsa onu kullan
                queueData: data.queueData || prev.queueData // ✨ CRM config ekle
              };
            }
            // Eğer incoming call state'i yoksa oluştur (timing issue)
            return {
              username: data.callerNumber,
              callId: data.callId,
              callerNumber: data.callerNumber,
              queueName: data.queue?.queueName || 'Gelen Çağrı',
              queueData: data.queueData // ✨ CRM config ekle
            };
          });
        } else {
          console.log('⚠️ Already have a real call ID, not updating:', currentId);
        }
      } else {
        console.log('⚠️ Socket event condition failed - agentExtension match:', data.agentExtension === agentExtension, 'has callId:', !!data.callId);
      }
    });

    // ✨ Dialer çağrısı agent'a bağlandı
    newSocket.on('dialer-call-connected', (data: {
      dialerCallId: string;
      campaignId: string;
      campaignName: string;
      contactId: string;
      phoneNumber: string;
      contactName?: string;
      contactCompany?: string;
      customFields?: Record<string, any>;
      attemptNumber: number;
      listId?: string;
    }) => {
      console.log('📞 Dialer call connected:', data);
      
      // Store dialer call info for disposition later
      setDialerCall({
        dialerCallId: data.dialerCallId,
        campaignId: data.campaignId,
        campaignName: data.campaignName,
        contactId: data.contactId,
        phoneNumber: data.phoneNumber,
        contactName: data.contactName,
        contactCompany: data.contactCompany,
        customFields: data.customFields,
        attemptNumber: data.attemptNumber,
        listId: data.listId
      });
      
      // Show notification
      setSnackbarMessage(`Dialer çağrısı: ${data.campaignName} - ${data.contactName || data.phoneNumber}`);
      setSnackbarOpen(true);
    });

    // ✨ Çağrı sonlandı (karşı taraf kapattı)
    newSocket.on('call-ended', async (data: {
      callId: string;
      callIdMongo?: string; // ✨ MongoDB _id (opsiyonel)
      reason: string;
      status?: string; // ✨ Call status (answered, ringing, waiting, etc.)
    }) => {
      console.log('📞 Call ended:', data);
      
      // ✨ ÖNEMLİ: Çağrı kapanmadan önce RTP akışını kontrol et
      // Eğer hâlâ ses akışı varsa, çağrı gerçekten kapanmamıştır
      if (freePBXWebRTCService) {
        try {
          const hasRTP = await freePBXWebRTCService.hasActiveRTPFlow();
          if (hasRTP) {
            console.warn('🔊 call-ended event received BUT RTP flow still active - IGNORING hangup event');
            return; // Çağrıyı kapatma, ses hâlâ akıyor
          }
        } catch (error) {
          console.error('❌ Error checking RTP flow:', error);
        }
      }
      
      // ✨ Multi-line check: Is this call on any of our lines?
      const endedCallId = data.callId || data.callIdMongo;
      const isLine1 = lines[1].callId === endedCallId;
      const isLine2 = lines[2].callId === endedCallId;
      const isCurrentCall = currentCallIdRef.current === endedCallId;
      
      console.log('🔍 Call ended event:', {
        endedCallId,
        currentCallId: currentCallIdRef.current,
        line1CallId: lines[1].callId,
        line2CallId: lines[2].callId,
        isLine1,
        isLine2,
        isCurrentCall,
        wasCallActive: isCallActiveRef.current
      });
      
      // ✨ Eğer çağrı hiç açılmadıysa (agent cevaplamadı) müşteri kartı açılmasın
      
      const shouldSkipCustomerCard = !callAnsweredRef.current; // Çağrı cevaplanmadıysa müşteri kartı açılmasın
      
      // ✨ Handle line-specific hangup
      if (isLine1 || isLine2) {
        const lineNum = isLine1 ? 1 : 2;
        console.log(`✅ Cleaning up Line ${lineNum}`);
        
        setLines(prev => ({
          ...prev,
          [lineNum]: {
            ...prev[lineNum],
            isActive: false,
            callId: null,
            phoneNumber: '',
            callDuration: 0
          }
        }));
        
        // If this was also the current call, clean up main UI
        if (isCurrentCall) {
          setIsCallActive(false);
          isCallActiveRef.current = false;
          
          // Close dialogs
          stopRingtone();
          setIncomingCall(null);
          setShowCallDialog(false);
          
          // Müşteri kartını kapat eğer agent cevaplamadıysa
          if (shouldSkipCustomerCard) {
            console.log('❌ Call not answered - NOT opening customer card');
            setShowCustomerCard(false);
            setCurrentCustomerCardData(null);
            setCurrentQueueData(null);
          }
            callAnsweredRef.current = false; // ✨ Sıfırla
        }
        
        return; // Exit early for line-specific cleanup
      }
      
      // ✨ Handle main call cleanup (backward compatibility)
      
      // ✨ Eğer çağrı cevaplanmadıysa müşteri kartı açma, statü zaten değişmedi o yüzden restore yapma
      if (shouldSkipCustomerCard) {
        console.log('❌ Call not answered - NOT opening customer card, status unchanged (no restore needed)');
        setShowCustomerCard(false);
        setCurrentCustomerCardData(null);
        setCurrentQueueData(null);
        callAnsweredRef.current = false; // ✨ Sıfırla
        // ✨ Statü restore YAPMA - çağrı cevaplanmadığı için statü hiç değişmedi
      }
      
      setIsCallActive((prevIsActive) => {
        const prevCallId = currentCallIdRef.current;
        
        // ✨ Eğer eşleşme varsa çağrıyı temizle
        if (endedCallId && prevCallId === endedCallId) {
          console.log('✅ Cleaning up call UI - keeping callId for finish code');
          
          // ✨ Stop ringtone when call ends
          stopRingtone();
          
          // ⚠️ currentCallId'yi KORUYORUZ - finish code için gerekli
          // setCurrentCallId(null); ❌ Bunu kaldırdık
          
          // ✨ currentCallPhoneNumber'ı temizleme - müşteri kartı görünür kalmalı
          // setCurrentCallPhoneNumber(''); // ❌ Bunu kaldırdık - müşteri kartı görünür kalmalı
          setIncomingCall(null);
          setShowCallDialog(false);
          // ✨ Müşteri kartını görünür tut - SADECE çağrı cevaplanmışsa
          if (!shouldSkipCustomerCard) {
            setShowCustomerCard(true);
            // ✨ Arama kapandığında "ACV" statüsüne geç (sadece cevaplanmışsa)
            setACVStatus();
            
            // ✨ Dialer çağrısı ise disposition dialog'u aç
            if (dialerCall) {
              console.log('📋 Opening disposition dialog for dialer call');
              setShowDispositionDialog(true);
            }
          }
          
          // ✨ PeerConnection'ı temizle (2. aramada ses olması için)
          if (freePBXWebRTCService) {
            try {
              // Sadece hangup yap, disconnect yapma (UserAgent'ı öldürme)
              freePBXWebRTCService.hangup();
              console.log('✅ Call hung up (UserAgent kept alive)');
            } catch (e) {
              console.error('❌ Error hanging up:', e);
            }
          }
          
          // Cleanup outbound PeerConnection
          const outboundPc = (window as any).outboundPeerConnection;
          if (outboundPc) {
            outboundPc.close();
            (window as any).outboundPeerConnection = null;
            (window as any).outboundCallId = null;
          }
          
          // Kullanıcıya telefon meşgul sesi ile bildir (dit dit dit)
          try {
            // Kısa beep sesi çal (gerçek telefon meşgul sesi: 2 beep, pause, 2 beep, pause, 2 beep)
            const playBeep = async () => {
              const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
              
              // Gerçek telefon meşgul sesi: 2 beep, pause, 2 beep, pause, 2 beep
              const beepPattern = [0, 0.25]; // Beep zamanları
              
              beepPattern.forEach(time => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.value = 480; // Hz
                oscillator.type = 'sine';
                
                const startTime = audioContext.currentTime + time;
                gainNode.gain.setValueAtTime(0.25, startTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
                
                oscillator.start(startTime);
                oscillator.stop(startTime + 0.2);
              });
            };
            
            playBeep();
          } catch (e) {
            console.log('Could not play beep:', e);
          }
          
          return false; // isCallActive'ı false yap
        } else {
          console.warn(`⚠️ Call ID mismatch - ignoring call-ended event. My Call: ${prevCallId}, Ended Call: ${data.callId || data.callIdMongo}`);
          return prevIsActive; // Değiştirme
        }
      });
    });

    // ==================== ACD MANAGEMENT ====================
    
    // Listen for agent status changes from supervisor
    newSocket.on('agent-status-change', (data: any) => {
      console.log('🔄 Agent status changed by supervisor:', data);
      console.log('🔍 Current user ID:', currentUser?._id);
      console.log('🔍 Match:', data.agentId === currentUser?._id);
      
      // Eğer bu agentın statüsü değiştirildiyse
      if (data.agentId === currentUser?._id) {
        const newStatusName = data.newStatusName || data.statusName;
        const newStatusId = data.newStatusId || data.statusId;
        
        console.log('✅ My status changed to:', newStatusName);
        console.log('🕐 Setting new start time and resetting duration');
        
        // Statüyü güncelle
        if (newStatusName) {
          setAgentStatus(newStatusName);
          
          // Context'teki currentStatusInfo'yu bul ve güncelle
          const newStatus = agentStatusContext.statuses.find((s: any) => 
            s._id === newStatusId || s.name === newStatusName
          );
          
          if (newStatus) {
            setCurrentStatusInfo(newStatus);
          }
          
          // Yeni statü için timer başlat - FORCE refresh
          const newStartTime = new Date();
          console.log('🕐 New start time:', newStartTime.toISOString());
          setStatusStartTime(newStartTime);
          setStatusDuration('00:00:00');
          
          // Snackbar göster
          setSnackbarMessage(`Statünüz "${newStatusName}" olarak değiştirildi`);
          setSnackbarOpen(true);
        }
      }
    });
    
    // Also listen for agentStatusUpdated (compatibility)
    newSocket.on('agentStatusUpdated', (data: any) => {
      console.log('🔄 Agent status updated:', data);
      console.log('🔍 Current user ID:', currentUser?._id);
      
      if (data.agentId === currentUser?._id) {
        const statusName = data.statusName;
        const statusId = data.statusId;
        
        console.log('✅ Status updated to:', statusName);
        
        if (statusName) {
          setAgentStatus(statusName);
          
          const status = agentStatusContext.statuses.find((s: any) => 
            s._id === statusId || s.name === statusName
          );
          
          if (status) {
            setCurrentStatusInfo(status);
          }
          
          // Yeni statü için timer başlat - FORCE refresh
          const newStartTime = new Date();
          console.log('🕐 New start time:', newStartTime.toISOString());
          setStatusStartTime(newStartTime);
          setStatusDuration('00:00:00');
          
          setSnackbarMessage(`Statünüz "${statusName}" olarak değiştirildi`);
          setSnackbarOpen(true);
        }
      }
    });
    
    // Backend assigns multiple queues to this agent
    newSocket.on('prepare-for-calls', async (data: {
      queues: Array<{ queueId: string; sipUri: string; authuser: string; secret: string }>;
    }) => {
      console.log('📋 ACD: Backend assigned queues:', data.queues);
      console.log('📋 ACD: Backend assigned queues (detailed):', JSON.stringify(data.queues, null, 2));

      try {
        // FreePBX: Queue registration is handled by backend
        // Frontend only needs to be ready to receive calls
        console.log(`📊 ACD: ${data.queues.length} queue(s) assigned by backend`);
        setSnackbarMessage(`${data.queues.length} kuyruk atandı - çağrılar size yönlendirilecek`);
        setSnackbarOpen(true);
      } catch (error: any) {
        console.error('❌ ACD: Error registering queues:', error);
        if (error?.response?.status === 401 || error?.message?.includes('token')) {
          console.log('🔒 Auth error in queue registration, logging out...');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          navigate('/login');
        }
      }
    });
    
    // Backend removes all queue assignments from this agent
    newSocket.on('unregister-all-queues', async (data: {
      queueIds: string[];
    }) => {
      console.log('📋 ACD: Backend removing all queue assignments:', data.queueIds);
      
      try {
        // FreePBX: Backend handles queue management
        console.log(`✅ ACD: Queues unregistered (backend managed)`);
      } catch (error) {
        console.error('❌ ACD: Error unregistering queues:', error);
      }
    });
    
    // ==================== END ACD MANAGEMENT ====================

    newSocket.on('call-accepted-notification', async (data: {
      callId: string;
      agentId: string;
      callerNumber: string;
      sipNumber: string;
    }) => {
      console.log('✅ Backend\'den çağrı kabul edildi bildirimi geldi:', data);
      
      try {
        
        // ✨ Stop ringtone when call is accepted
        stopRingtone();
        
        // Close incoming call dialog
        setIncomingCall(null);
        setShowCallDialog(false);
        
        // Set call as active
        setIsCallActive(true);
        setCallDuration(0);
        setCurrentCallPhoneNumber(data.callerNumber);
        setShowCustomerCard(true); // ✨ Müşteri kartını aç
        
        console.log('✅ Call accepted successfully - Agent can answer using their phone');
    
        // Show notification to user
        alert(`Çağrı kabul edildi! ${data.callerNumber} numarasından gelen çağrıyı telefonunuzdan cevaplayabilirsiniz.`);
        
      } catch (error: any) {
        console.error('❌ Error in call-accepted-notification:', error);
        if (error?.response?.status === 401 || error?.message?.includes('token')) {
          console.log('🔒 Auth error in call handling, logging out...');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          navigate('/login');
        }
      }
    });

    // ✨ Messaging events
    newSocket.on('new-message', (data: any) => {
      console.log('💬 New message received:', data);
      // Reload conversations to update last message
      loadConversations();
      
      // If viewing this conversation, add message to UI immediately
      if (selectedUser) {
        const isInvolvedInMessage = 
          (data.senderId === currentUser?._id && data.receiverId === selectedUser._id) ||
          (data.senderId === selectedUser._id && data.receiverId === currentUser?._id);
        
        if (isInvolvedInMessage) {
          // Add message to current conversation immediately
          setMessages(prev => {
            // Check if message already exists (avoid duplicates)
            if (prev.some(msg => msg._id === data.messageId)) {
              return prev;
            }
            // Add new message with populated user data
            return [...prev, {
              _id: data.messageId,
              senderId: { _id: data.senderId },
              receiverId: { _id: data.receiverId },
              content: data.content,
              isRead: false,
              createdAt: new Date().toISOString()
            }];
          });
        }
      }
      
      // Update unread count (only if current user is the receiver)
      if (data.receiverId === currentUser?._id) {
        loadUnreadCount();
        // Show toast notification only if not viewing this conversation
        if (!selectedUser || selectedUser._id !== data.senderId) {
          setSnackbarMessage(`Yeni mesaj: ${data.content.substring(0, 30)}...`);
          setSnackbarOpen(true);
        }
      }
    });

    newSocket.on('message-read-receipt', (data: any) => {
      console.log('✓ Message read:', data.messageId);
      // Update message read status in UI
      setMessages(prev => prev.map(msg => 
        msg._id === data.messageId ? { ...msg, isRead: true } : msg
      ));
    });

    newSocket.on('user-typing', (data: any) => {
      setTypingUsers(prev => new Set(prev).add(data.userId));
      // Remove typing indicator after 3 seconds
      setTimeout(() => {
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      }, 3000);
    });

    newSocket.on('user-stop-typing', (data: any) => {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(data.userId);
        return newSet;
      });
    });

    // ✨ Periyodik çağrı durumu senkronizasyonu (her 10 saniyede bir - RTP kontrolü için daha sık)
    const syncInterval = setInterval(async () => {
      try {
        if (!currentUser?._id) return;
        
        // Backend'den aktif çağrıları çek
        const response = await callAPI.getActive();
        const activeCalls = response?.data?.data || response?.data || [];
        
        // Agent'ın aktif çağrısını bul
        const agentActiveCall = activeCalls.find((call: any) => {
          const callAgentId = typeof call.agentId === 'object' ? call.agentId._id : call.agentId;
          return callAgentId === currentUser._id;
        });
        
        // ✨ RTP Flow kontrolü - Bu en önemli kontrol
        if (freePBXWebRTCService) {
          const hasRTP = await freePBXWebRTCService.hasActiveRTPFlow();
          const currentIsActive = isCallActiveRef.current;
          const currentCallId = currentCallIdRef.current;
          
          if (hasRTP) {
            if (!currentIsActive) {
              console.warn('🔊 RTP Flow detected but UI shows no active call - Restoring Call UI');
              setIsCallActive(true);
              isCallActiveRef.current = true;
        callAnsweredRef.current = true; // ✨ Outbound çağrı - finish code açılsın
              setShowCustomerCard(true);
              
              // ✨ Eğer callId yoksa, backend'den gerçek ID'yi bul
              if (!currentCallId) {
                // Backend'den bu agent'ın aktif çağrısını bul
                if (agentActiveCall && agentActiveCall._id) {
                  console.log(`✅ Found backend call ID for restored call: ${agentActiveCall._id}`);
                  setCurrentCallId(agentActiveCall._id);
                  currentCallIdRef.current = agentActiveCall._id;
                  
                  // Telefon numarasını da set et
                  const phoneNum = agentActiveCall.callerNumber || agentActiveCall.calledNumber || '';
                  setCurrentCallPhoneNumber(phoneNum);
                } else {
                  // Backend'de de yok ama RTP var - geçici ID ata (fallback)
                  console.warn('⚠️ No backend call found but RTP active - using temporary ID');
                  const tempId = 'restored-' + Date.now();
                  setCurrentCallId(tempId);
                  currentCallIdRef.current = tempId;
                }
              }
            }
            // RTP varsa backend sync'i atlayabiliriz - çağrı kesinlikle aktif
            return;
          } else if (currentIsActive) {
            // RTP yok ama UI aktif gösteriyor - belki çağrı gerçekten kapandı
            console.log('⚠️ No RTP flow detected but UI shows active call - checking backend...');
          }
        }
        
        // ✨ Ref'lerden güncel değerleri al
        const currentIsActive = isCallActiveRef.current;
        const currentCallId = currentCallIdRef.current;
        const callStartTime = callStartTimeRef.current;
        
        // Eğer backend'de aktif çağrı yoksa ama frontend'de varsa, çağrıyı kapat
        // ANCAK: Çağrı yeni başladıysa (ilk 15 saniye) bekle - backend henüz kayıt oluşturmamış olabilir
        if (!agentActiveCall && currentIsActive && currentCallId) {
          const callAge = callStartTime ? (Date.now() - callStartTime) / 1000 : 0;
          
          if (callAge < 15) {
            console.log(`⏳ Call sync: Call is too new (${callAge.toFixed(1)}s), waiting for backend...`);
            return; // Çağrı çok yeni, backend'in kayıt oluşturmasını bekle
          }
          
          console.warn('⚠️ Call sync: Backend has no active call but frontend shows active call - IGNORING sync to prevent accidental hangup (Fix Active)');
          // setIsCallActive(false);
          // isCallActiveRef.current = false;
          // ⚠️ currentCallId'yi KORUYORUZ - finish code için gerekli
          // setCurrentCallId(null); ❌ Bunu kaldırdık
          // currentCallIdRef.current = null; ❌ Bunu kaldırdık
          // ✨ currentCallPhoneNumber'ı temizleme - müşteri kartı görünür kalmalı
          // setCurrentCallPhoneNumber(''); // ❌ Bunu kaldırdık - müşteri kartı görünür kalmalı
          // setIncomingCall(null);
          // setShowCallDialog(false);
          // setShowCustomerCard(true); // ✨ Müşteri kartını görünür tut
          // stopRingtone();
          
          // PeerConnection'ı temizle
          /*
          if (freePBXWebRTCService) {
            try {
              // Sadece hangup yap, disconnect yapma (UserAgent'ı öldürme)
              freePBXWebRTCService.hangup();
            } catch (e) {
              console.error('❌ Error cleaning up peer connection:', e);
            }
          }
          */
        }
        
        // Eğer backend'de aktif çağrı varsa ama frontend'de yoksa, çağrıyı güncelle
        /*if (agentActiveCall && !currentIsActive && agentActiveCall.callerNumber == agentActiveCall.calledNumber ) {
          console.log('✅ Call sync: Backend has active call but frontend shows no call - updating UI');
          const callId = agentActiveCall._id || agentActiveCall.callId;
          const phoneNumber = agentActiveCall.callerNumber || agentActiveCall.calledNumber || '';
          
          setCurrentCallId(callId);
          currentCallIdRef.current = callId;
          setCurrentCallPhoneNumber(phoneNumber);
          setIsCallActive(true);
          setShowCustomerCard(true); // ✨ Müşteri kartını aç
          isCallActiveRef.current = true;
        callAnsweredRef.current = true; // ✨ Outbound çağrı - finish code açılsın
        }*/
        
        // Eğer backend'de çağrı durumu değişmişse (ringing -> answered gibi), güncelle
        if (agentActiveCall && currentCallId) {
          const callId = agentActiveCall._id || agentActiveCall.callId;
          if (callId === currentCallId && agentActiveCall.status === 'ended' && currentIsActive) {
            console.warn('⚠️ Call sync: Backend shows call ended but frontend shows active - checking RTP before cleanup');
            
            // ✨ RTP kontrolü yap - ses varsa çağrıyı kapatma
            if (freePBXWebRTCService) {
              const hasRTP = await freePBXWebRTCService.hasActiveRTPFlow();
              if (hasRTP) {
                console.warn('🔊 Backend says ended but RTP flow still active - KEEPING call active');
                return; // Çağrıyı kapatma
              }
            }
            
            setIsCallActive(false);
            isCallActiveRef.current = false;
            // ⚠️ currentCallId'yi KORUYORUZ - finish code için gerekli
            // setCurrentCallId(null); ❌ Bunu kaldırdık
            // currentCallIdRef.current = null; ❌ Bunu kaldırdık
            // ✨ currentCallPhoneNumber'ı temizleme - müşteri kartı görünür kalmalı
            // setCurrentCallPhoneNumber(''); // ❌ Bunu kaldırdık - müşteri kartı görünür kalmalı
            setIncomingCall(null);
            setShowCallDialog(false);
            setShowCustomerCard(true); // ✨ Müşteri kartını görünür tut
            stopRingtone();
          }
        }
      } catch (error) {
        console.error('❌ Error syncing call status:', error);
      }
    }, 10000); // 10 saniye (RTP kontrolü için daha sık)

    return () => {
      newSocket.off('incoming-call');
      newSocket.off('call-ended');
      newSocket.off('call-answered');
      newSocket.close();
      clearInterval(syncInterval); // ✨ Interval'i temizle
    };
  }, [currentUser]); // Only depend on currentUser

  // ✨ Ref'leri state ile senkronize et
  useEffect(() => {
    isCallActiveRef.current = isCallActive;
  }, [isCallActive]);
  
  useEffect(() => {
    currentCallIdRef.current = currentCallId;
  }, [currentCallId]);

  // ✨ Stop ringtone sound
  const stopRingtone = () => {
    if (ringtoneAudioRef.current) {
      try {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current.currentTime = 0;
        ringtoneAudioRef.current = null;
        setRingtoneAudio(null);
        console.log('🔇 Ringtone stopped');
      } catch (error) {
        console.error('❌ Error stopping ringtone:', error);
      }
    }
  };

  // Eski FreePBX service kodu kaldırıldı - yeni FreePBX WebRTC servisi yukarıda initialize ediliyor

  // Handle call answer
  const handleAnswerCall = async () => {
    if (!incomingCall) return;

    try {
      console.log('✅ Answering call from:', incomingCall.callerNumber);
      callAnsweredRef.current = true; // ✨ Çağrı cevaplanıyor
      
      // ✨ Mevcut statüyü sakla ve "Aramada" statüsüne geç
      await setCallingStatus();
      
      // Zil sesini durdur
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current.currentTime = 0;
      }
      
      // FreePBX WebRTC ile çağrıyı kabul et
      const invitation = (window as any)._currentInvitation;
      if (invitation) {
        await answerCall(invitation);
        
        // ✨ Update lines state with the new call ID
        setLines(prev => ({
          ...prev,
          [activeLine]: {
            ...prev[activeLine],
            status: 'connected',
            sipCallId: invitation.id,
            number: incomingCall.callerNumber,
            duration: 0
          }
        }));

        (window as any)._currentInvitation = null;
      }
      
      // Çağrı başlangıç zamanını kaydet
      callStartTimeRef.current = Date.now();
      
      // ✨ Önce queueData'yı set et (CustomerCard açılmadan önce!)
      if (incomingCall.queueData && !currentQueueData) {
        console.log('📋 Setting queue CRM config from incomingCall BEFORE opening CustomerCard:', incomingCall.queueData);
        setCurrentQueueData(incomingCall.queueData);
      }
      
      // UI'ı güncelle
      setCurrentCallId(incomingCall.callId);
      currentCallIdRef.current = incomingCall.callId;
      setCurrentCallPhoneNumber(incomingCall.callerNumber);
      setShowCustomerCard(true);
      setShowCallDialog(false);
      setIncomingCall(null);
      
      // ✨ Queue CRM config'ini al (eğer henüz alınmadıysa - fallback)
      if (!currentQueueData && !incomingCall.queueData && incomingCall.callId) {
        (async () => {
          try {
            // Call detaylarını al
            const callResponse = await fetch(`${API_URL}/calls/${incomingCall.callId}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              }
            });
            if (callResponse.ok) {
              const callDetails = await callResponse.json();
              const queueId = callDetails.queueId?._id || callDetails.queueId;
              
              if (queueId) {
                // Queue detaylarını al
                const queueResponse = await fetch(`${API_URL}/queues/${queueId}`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                  }
                });
                if (queueResponse.ok) {
                  const queueDetails = await queueResponse.json();
                  console.log('📋 Queue CRM config fetched on answer:', queueDetails);
                  setCurrentQueueData({
                    customerCardDatasetListUrl: queueDetails.customerCardDatasetListUrl,
                    customerCardDatasetSchemaUrl: queueDetails.customerCardDatasetSchemaUrl,
                    customerCardDatasetAddUrl: queueDetails.customerCardDatasetAddUrl,
                    customerCardSelectedDatasetId: queueDetails.customerCardSelectedDatasetId,
                    customerCardApiUrl: queueDetails.customerCardApiUrl
                  });
                }
              }
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch queue config on answer:', err);
          }
        })();
      }
      
      console.log('✅ Call answered successfully');
    } catch (error) {
      console.error('❌ Error accepting call:', error);
      alert('Çağrı kabul edilirken hata oluştu: ' + (error as Error).message);
    }
  };

  // Handle call reject
  const handleRejectCall = async () => {
    if (!incomingCall) return;

    try {
      console.log('❌ Rejecting call');
      
      // Zil sesini durdur
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current.currentTime = 0;
      }
      
      // Invitation varsa reddet
      const invitation = (window as any)._currentInvitation;
      if (invitation) {
        await freePBXWebRTCService.rejectCall(invitation);
        (window as any)._currentInvitation = null;
      }
      
      // Backend'e bildir
      if (currentCallId) {
        const response = await fetch(`${API_URL}/agent/call/${currentCallId}/reject`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({ 
            reason: 'agent_declined',
            agentId: agentId || currentUser?.userId
          })
        });
        
        if (response.ok) {
          console.log('✅ Call rejected successfully');
        }
      }
      
      setShowCallDialog(false);
      setIncomingCall(null);
      setCurrentCallId(null);
      callAnsweredRef.current = false; // ✨ Sıfırla - çağrı cevaplanmadan reddedildi
      
    } catch (error: any) {
      console.error('❌ Error rejecting call:', error);
      if (error?.response?.status === 401 || error?.message?.includes('token')) {
        console.log('🔒 Auth error while rejecting call, logging out...');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      }
    }
  };







  // Logout function
  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  // Çağrıyı kabul et - bu fonksiyon artık yukarıda tanımlı (answerCall olarak)



  useEffect(() => {
    if (currentUser) {
      // Set default queue if user has assigned ones
      const userQueues = queueContext.queues.filter(
        queue => currentUser.queueIds?.some((queueId: any) => 
          typeof queueId === 'object' ? queueId._id === queue._id : queueId === queue._id
        )
      );
      
      if (userQueues.length > 0) {
        // Don't auto-select first queue, let user choose
        // if (!selectedQueue) {
        //   setSelectedQueue(userQueues[0]._id);
        // }
        
        // Register agent for ALL their queues with ACD
        if (socket) {
          const queueIds = userQueues.map(q => q._id);
          console.log('📋 ACD: Registering agent for queues:', queueIds);
          
          socket.emit('agent-available', {
            agentId: currentUser._id,
            queueIds: queueIds,
            handleId: null,
            sessionId: null
          });
        }
      }
    }
  }, [currentUser, queueContext.queues, socket]);

  // Check SIP registration status when queue changes
  useEffect(() => {
    
  }, [selectedQueue, queueContext.queues, sipNumberContext.sipNumbers]);

  const makeCall = async (customPhoneNumber?: string) => {
    const numberToCall = customPhoneNumber || phoneNumber;
    
    // Ensure numberToCall is a string before calling trim
    if (!numberToCall || typeof numberToCall !== 'string' || !numberToCall.trim()) {
      alert('Lütfen telefon numarası girin');
      return;
    }

    // Eğer finish code bekleyen bir çağrı varsa yeni çağrı yapılamaz
    if (isPendingFinishCode && showCustomerCard) {
      alert('⚠️ Lütfen önce mevcut çağrı için müşteri kartını doldurun ve finish code seçin!');
      return;
    }

    try {
      // Find the selected queue to get its prefix
      const queue = queueContext.queues.find(q => q._id === selectedQueue);

      console.log('🔍 Queue lookup:', {
        selectedQueueId: selectedQueue,
        foundQueue: queue,
        outboundPrefix: queue?.outboundPrefix
      });
      
      const prefix = queue?.outboundPrefix || '';
      
      // Add prefix to phone number if exists
      const finalPhoneNumber = prefix ? `${prefix}${numberToCall}` : numberToCall;
      
      // ✨ Multi-line: Check if we need to switch lines
      console.log('📞 Current line state:', {
        activeLine,
        line1Active: lines[1].isActive,
        line2Active: lines[2].isActive,
        line1CallId: lines[1].callId,
        line2CallId: lines[2].callId
      });
         try {
            await freePBXWebRTCService.holdCall();
            updateActiveLine({ isHold: true });
            setIsOnHold(true);
            console.log('✅ Hold successful');
          } catch (error) {
            console.error('❌ Failed to hold call:', error);
            alert('Çağrı beklemeye alınamadı. Lütfen tekrar deneyin.');
            return;
          }
      // ✨ Eğer aktif hat'ta çağrı varsa, diğer hat'a geç
      if (lines[activeLine].isActive && lines[activeLine].callId) {
        const otherLine = activeLine === 1 ? 2 : 1;
        
        // Diğer hat boş mu kontrol et
        if (!lines[otherLine].isActive) {
          console.log(`✨ Line ${activeLine} is active, switching to Line ${otherLine}`);
          
          // Aktif çağrıyı beklemeye al
          console.log(`⏸️ Putting Line ${activeLine} call on hold...`);
          try {
            await freePBXWebRTCService.holdCall();
            updateActiveLine({ isHold: true });
            setIsOnHold(true);
            console.log('✅ Hold successful');
          } catch (error) {
            console.error('❌ Failed to hold call:', error);
            alert('Çağrı beklemeye alınamadı. Lütfen tekrar deneyin.');
            return;
          }
          
          // Diğer hat'a geç
          setActiveLine(otherLine as 1 | 2);
          console.log(`✨ Switched to Line ${otherLine}`);
        } else {
          alert('⚠️ Her iki hat da meşgul! Lütfen bir çağrıyı sonlandırın.');
          return;
        }
      }
      
      console.log('📞 Making call:', {
        originalNumber: phoneNumber,
        prefix: prefix,
        finalNumber: finalPhoneNumber,
        queue: queue?.name,
        activeLine
      });
      
      // ✨ Kontrol: agentId ve extension boş olabilir
      const finalAgentId = agentId || currentUser?._id || currentUser?.userId;
      const finalExtension = agentExtension || localStorage.getItem('agentExtension');
      
      if (!finalAgentId) {
        throw new Error('Agent ID bulunamadı. Lütfen sayfayı yenileyin.');
      }
      
      if (!finalExtension) {
        throw new Error('Agent extension bulunamadı. Lütfen sayfayı yenileyin.');
      }
      
      console.log('📞 Sending to backend:', {
        phoneNumber: finalPhoneNumber,
        agentId: finalAgentId,
        extension: finalExtension,
        queueId: selectedQueue
      });
      
      // Backend'e çağrı başlatma isteği gönder
      // Backend'e hem prefix'li numara (arama için) hem de orijinal numara (CRM için) gönder
      const response = await fetch(`${API_URL}/agent/call/make`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({ 
          phoneNumber: finalPhoneNumber, // Prefix'li (arama için)
          originalPhoneNumber: numberToCall, // Prefix'siz (CRM için)
          agentId: finalAgentId,
          extension: finalExtension,
          queueId: selectedQueue
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error:', response.status, errorText);
        throw new Error(`Backend error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('📋 Backend response:', data);
      
      if (data.success) {
        // ✨ ÖNCE callId'yi set et - Backend response gelir gelmez
        const backendCallId = data.callId || `call-${Date.now()}`;
        setCurrentCallId(backendCallId);
        currentCallIdRef.current = backendCallId;
        
        // Çağrı başlangıç zamanını kaydet (senkronizasyon için)
        callStartTimeRef.current = Date.now();
        
        // ✨ Store external customer data and queue config BEFORE opening card
        if (data.customerCardData) {
          console.log('📋 External customer data received for outbound:', data.customerCardData);
          setCurrentCustomerCardData(data.customerCardData);
        } else {
          // Eğer CRM verisi yoksa temizle
          setCurrentCustomerCardData(null);
        }
        
        if (data.queueData) {
          console.log('⚙️ Queue CRM config received for outbound:', data.queueData);
          setCurrentQueueData(data.queueData);
        } else {
          setCurrentQueueData(null);
        }
        
        // FreePBX WebRTC üzerinden çağrı yap (prefix'li numara ile)
        console.log('☎️ Initiating SIP call via WebRTC...');
        const sipCallId = await freePBXWebRTCService.makeCall(finalPhoneNumber);
        
        // ✨ Agent status'ünü "Aramada" yap
        await setCallingStatus();
        
        // Müşteri kartında gösterilecek numarayı set et (prefix olmadan)
        // customPhoneNumber parametresi kullanıldıysa onu, yoksa input'taki phoneNumber'ı kullan
        const displayNumber = customPhoneNumber || phoneNumber;
        setCurrentCallPhoneNumber(displayNumber);
        setIsCallActive(true);
        
        // ✨ Müşteri kartını yalnızca kapalıysa aç (2 kart açılmasını önle)
        // Small delay to ensure state updates are processed
        setTimeout(() => {
          if (!showCustomerCard) {
            setShowCustomerCard(true);
          }
        }, 100);
        
        // ✨ Store SIP Call ID ve update active line
        updateActiveLine({ 
          sipCallId,
          isActive: true,
          callId: backendCallId,
          currentCallPhoneNumber: displayNumber
        });
        
        isCallActiveRef.current = true;
        callAnsweredRef.current = true; // ✨ Outbound çağrı - finish code açılsın
        setCallDuration(0);
        setPhoneNumber('');
        
        console.log('✅ Call initiated successfully on Line', activeLine);
      } else {
        throw new Error(data.error || data.message || 'Çağrı başlatılamadı');
      }
    } catch (error: any) {
      console.error('❌ Call error:', error);
      // Check if it's an auth error
      if (error?.response?.status === 401 || error?.message?.includes('token')) {
        console.log('🔒 Auth error during call, logging out...');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
        return;
      }
      alert('Çağrı sırasında hata oluştu: ' + (error as Error).message);
    }
  };

  // Answer incoming call
  const answerCall = async (invitation?: Invitation) => {
    try {
      // Eğer finish code bekleyen bir çağrı varsa yeni çağrı cevaplanamaz
      if (isPendingFinishCode && showCustomerCard) {
        alert('⚠️ Lütfen önce mevcut çağrı için müşteri kartını doldurun ve finish code seçin!');
        return;
      }

      console.log('✅ Answering call');
      
      if (invitation) {
        await freePBXWebRTCService.acceptCall(invitation);
      }
      
      // Backend'e bildir
      if (currentCallId) {
        await fetch(`${API_URL}/agent/call/${currentCallId}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({
            agentId: agentId || currentUser?.userId,
            extension: agentExtension
          })
        });
      }
      
      // Çağrı başlangıç zamanını kaydet
      callStartTimeRef.current = Date.now();
      
      setIsCallActive(true);
      isCallActiveRef.current = true;
        callAnsweredRef.current = true; // ✨ Outbound çağrı - finish code açılsın
      setCallDuration(0);
      setShowCallDialog(false);
      setIncomingCall(null);
      
      // ✨ Agent status'ünü "Aramada" yap
      await setCallingStatus();
      
      // Zil sesini durdur
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current.currentTime = 0;
      }
      
      console.log('✅ Call answered successfully');
    } catch (error) {
      console.error('❌ Answer call error:', error);
      alert('Çağrı cevaplanamadı');
    }
  };

  // ✨ Dialer disposition kaydet
  const handleSaveDisposition = async () => {
    if (!dialerCall || !selectedDisposition) {
      return;
    }
    
    setSavingDisposition(true);
    try {
      await dialerAPI.saveDisposition(
        dialerCall.dialerCallId,
        selectedDisposition,
        dispositionNotes
      );
      
      console.log('✅ Disposition saved:', selectedDisposition);
      setSnackbarMessage('Sonuç kaydedildi');
      setSnackbarOpen(true);
      
      // Dialog'u kapat ve state'leri temizle
      setShowDispositionDialog(false);
      setSelectedDisposition('');
      setDispositionNotes('');
      setDialerCall(null);
      
    } catch (error) {
      console.error('❌ Error saving disposition:', error);
      setSnackbarMessage('Sonuç kaydedilemedi');
      setSnackbarOpen(true);
    } finally {
      setSavingDisposition(false);
    }
  };

  // Hangup call - Müşteri kartı açık kalacak, finish code orada seçilecek
  const hangupCall = async () => {
    try {
      console.log('📴 Hanging up call - customer card will stay open for finish code');
      console.log('Current callId:', currentCallId);
      
      // 1️⃣ FreePBX WebRTC'de çağrıyı bitir
      const activeSipCallId = lines[activeLine].sipCallId;
      await freePBXWebRTCService.hangup(activeSipCallId || undefined);
      
      // 2️⃣ RTP akışını zorla temizle
      try {
        const session = freePBXWebRTCService.getCurrentSession();
        if (session) {
          // Tüm media streamleri durdur
          const sessionDescriptionHandler = session.sessionDescriptionHandler as any;
          const peerConnection = sessionDescriptionHandler?.peerConnection;
          if (peerConnection) {
            console.log('🔇 Stopping all RTP streams...');
            peerConnection.getSenders().forEach((sender: any) => {
              if (sender.track) {
                sender.track.stop();
              }
            });
            peerConnection.getReceivers().forEach((receiver: any) => {
              if (receiver.track) {
                receiver.track.stop();
              }
            });
            console.log('✅ RTP streams stopped');
          }
        }
      } catch (err) {
        console.warn('⚠️ Error stopping RTP streams:', err);
      }
      
      // 3️⃣ Backend'e hangup isteği gönder (tüm kanalları kapatması için)
      if (currentCallId && !currentCallId.startsWith('incoming-')) {
        try {
          await fetch(`${API_URL}/freepbx/hangup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            },
            body: JSON.stringify({
              callId: currentCallId
            })
          });
          console.log('✅ Backend hangup request sent');
        } catch (err) {
          console.warn('⚠️ Backend hangup request failed:', err);
        }
      }
      
      // 4️⃣ UI'da call'ı pasif yap ama müşteri kartı açık kalsın
      setIsCallActive(false);
      isCallActiveRef.current = false;
      setCallDuration(0);
      setIsMuted(false);
      setIsOnHold(false);
      
      // ⚠️ currentCallId ve currentCallPhoneNumber'i KORUYORUZ - finish code için gerekli
      
      // Müşteri kartı açık kalacak ve finish code seçimi bekleyecek
      setIsPendingFinishCode(true); // Yeni çağrı yapılmasını engelle
      console.log('✅ Call ended, customer card open for finish code selection');
    } catch (error) {
      console.error('❌ Hangup call error:', error);
      alert('Çağrı sonlandırılamadı');
    }
  };

  // Finish code ile çağrıyı tamamen sonlandır (CustomerCard'dan çağrılacak)
  const handleFinishCodeSelected = async (finishCode: string) => {
    console.log('🎯 handleFinishCodeSelected called with:', finishCode);
    let callIdToUse = currentCallIdRef.current; // Use ref for most current value
    console.log('Current callId:', callIdToUse);
    
    // 🔄 Fallback: Eğer hala temp ID varsa, backend'den son çağrıyı bulmaya çalış
    if (!callIdToUse || callIdToUse.startsWith('incoming-')) {
      console.log('⚠️ Still using temp ID or no ID, trying to find real ID from backend...');
      try {
        // Son çağrıyı getir (agentId'ye göre)
        const response = await fetch(`${API_URL}/calls?agentId=${currentUser?._id}&limit=1`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.length > 0) {
            const lastCall = data.data[0];
            // Çağrının yeni olup olmadığını kontrol et (son 5 dakika)
            const callTime = new Date(lastCall.startTime).getTime();
            if (Date.now() - callTime < 5 * 60 * 1000) {
               console.log('✅ Found recent call from backend:', lastCall._id);
               callIdToUse = lastCall._id;
            } else {
              console.warn('⚠️ Last call is too old:', lastCall.startTime);
            }
          }
        }
      } catch (err) {
        console.error('❌ Failed to fetch last call fallback:', err);
      }
    }
    
    if (!callIdToUse) {
      console.error('❌ No call ID available!');
      alert('Çağrı ID bulunamadı');
      return;
    }

    try {
      console.log(`📤 Sending finish code to backend for call ${callIdToUse}...`);
      // Backend'e finish code ile bildir
      const response = await fetch(`${API_URL}/calls/${callIdToUse}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          finishCode: finishCode
        })
      });
      
      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }
      
      console.log('✅ Backend response OK');
      
      // Artık müşteri kartı kapanabilir
      setCurrentCallPhoneNumber('');
      setCurrentCallId(null);
      currentCallIdRef.current = null;
      callStartTimeRef.current = null;
      setShowCustomerCard(false);
      setIsPendingFinishCode(false); // Artık yeni çağrı yapılabilir
      callAnsweredRef.current = false; // ✨ Sonraki çağrı için sıfırla
      
      // ✨ Önceki statüye geri dön
      console.log('🔄 Restoring previous status after finish code...');
      await restorePreviousStatus();
      
      console.log('✅ Call ended with finish code:', finishCode);
    } catch (error) {
      console.error('❌ Complete call end error:', error);
      alert('Çağrı kaydedilemedi, tekrar deneyin');
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const newMutedState = !isMuted;
    freePBXWebRTCService.setMuted(newMutedState);
    setIsMuted(newMutedState);
    console.log(newMutedState ? '🔇 Muted' : '🔊 Unmuted');
  };

  // Toggle hold
  const toggleHold = async () => {
    try {
      const newHoldState = !isOnHold;
      
      // ✨ Multi-line: Get the SIP Call ID of the active line
      const activeSipCallId = lines[activeLine].sipCallId;
      console.log('🔍 Toggle hold for line', activeLine, 'SIP Call ID:', activeSipCallId);
      
      if (newHoldState) {
        // Put call on hold
        await freePBXWebRTCService.holdCall();
        console.log('⏸️ Call put on hold on Line', activeLine);
      } else {
        // Resume call
        await freePBXWebRTCService.unholdCall();
        console.log('▶️ Call resumed on Line', activeLine);
      }
      
      // ✨ Update the hold state for the active line
      updateActiveLine({ isHold: newHoldState });
      setIsOnHold(newHoldState);
      
      // Backend'e bildir
      if (currentCallId) {
        await fetch(`${API_URL}/agent/call/${currentCallId}/hold`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({ 
            isOnHold: newHoldState,
            agentId: agentId || currentUser?.userId
          })
        });
      }
    } catch (error) {
      console.error('❌ Toggle hold error:', error);
      alert('Beklemeye alma işlemi başarısız oldu');
    }
  };

  // ✨ Merge Calls (Conference)
  const mergeCalls = async () => {
    const callId1 = lines[1].callId;
    const callId2 = lines[2].callId;

    if (!callId1 || !callId2) {
      alert('Konferans için her iki hatta da aktif çağrı olmalıdır.');
      return;
    }

    // ✨ Check if either call is using temporary ID (not yet synced with backend)
    const isTemp1 = callId1.startsWith('incoming-') || callId1.startsWith('outbound-') || callId1.startsWith('restored-');
    const isTemp2 = callId2.startsWith('incoming-') || callId2.startsWith('outbound-') || callId2.startsWith('restored-');
    
    if (isTemp1 || isTemp2) {
      const tempLines = [];
      if (isTemp1) tempLines.push('Hat 1');
      if (isTemp2) tempLines.push('Hat 2');
      
      console.warn(`⚠️ Temporary call IDs detected: ${tempLines.join(', ')}`);
      alert(`${tempLines.join(' ve ')} henüz backend ile senkronize olmadı. Lütfen 1-2 saniye bekleyip tekrar deneyin.`);
      return;
    }

    try {
      console.log('🔗 Merging calls:', callId1, callId2);
      
      const response = await callAPI.mergeCalls(callId1, callId2);
      
      if (response.data.success) {
        alert('Çağrılar birleştirildi (Konferans Başladı)');
        // UI güncellemesi gerekebilir (örneğin "Konferans" ikonu göstermek)
      } else {
        alert('Çağrılar birleştirilemedi: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('❌ Merge calls error:', error);
      const errorMsg = error.response?.data?.message || 'Konferans başlatılırken hata oluştu';
      alert(errorMsg);
    }
  };

  // Call duration timer for both lines
  useEffect(() => {
    const interval = setInterval(() => {
      setLines(prev => {
        const newLines = { ...prev };
        let changed = false;

        if (newLines[1].isActive) {
          newLines[1] = { ...newLines[1], callDuration: newLines[1].callDuration + 1 };
          changed = true;
        }
        if (newLines[2].isActive) {
          newLines[2] = { ...newLines[2], callDuration: newLines[2].callDuration + 1 };
          changed = true;
        }

        return changed ? newLines : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Load available statuses
  useEffect(() => {
    console.log('🔍 AgentPanel: Agent statuses changed:', agentStatusContext.statuses);
    console.log('🔍 AgentPanel: Statuses length:', agentStatusContext.statuses.length);
    if (agentStatusContext.statuses.length > 0) {
      // ✨ Sadece 'available' tipindeki statüleri göster (agent'ın seçebileceği statüler)
      const selectableStatuses = agentStatusContext.statuses.filter((s: any) => s.type === 'available');
      setAvailableStatuses(selectableStatuses);
      console.log('✅ AgentPanel: Available statuses set (type=available only):', selectableStatuses);

      // ✨ Login'de otomatik olarak "Hazır" statüsüne geç
      let initialStatus = selectableStatuses.find((s: any) => s.name === 'Hazır');
      
      // Eğer "Hazır" bulunamazsa alternatif isimleri dene
      if (!initialStatus) {
        initialStatus = selectableStatuses.find((s: any) => s.name === 'Ready' || s.name === 'online');
      }
      
      // Hala bulunamadıysa ilk available status'ü kullan
      if (!initialStatus) {
        initialStatus = selectableStatuses[0];
      }
      
      if (initialStatus) {
        console.log('✅ AgentPanel: Auto-setting status to "Hazır" on login:', initialStatus);
        
        // Backend'e status değişikliğini gönder (async wrapper)
        (async () => {
          const success = await agentStatusContext.setAgentStatus(currentUser._id, initialStatus._id);
          
          if (success) {
            setCurrentStatusInfo(initialStatus);
            setAgentStatus(initialStatus.name);
            
            // ✨ Süreyi sıfırla
            const now = new Date();
            setStatusStartTime(now);
            setStatusDuration('00:00:00');
            
            console.log('✅ Status set to "Hazır" with duration 00:00:00');
          }
        })();
      }
    } else {
      console.log('⚠️ AgentPanel: No statuses available');
    }
  }, [agentStatusContext.statuses, currentUser?.currentStatusId]);

  // Change agent status
  const changeAgentStatus = async (newStatusName: string, savePrevious: boolean = false) => {
    const newStatus = agentStatusContext.getStatusByName(newStatusName);
    if (!newStatus) {
      console.warn(`⚠️ Status not found: ${newStatusName}`);
      return;
    }

    console.log('🔍 AgentPanel: Changing status from', currentStatusInfo?.name, 'to', newStatusName);
    console.log('🔍 AgentPanel: Current status info:', currentStatusInfo);
    console.log('🔍 AgentPanel: New status:', newStatus);
    console.log('🔍 AgentPanel: Allowed transitions:', currentStatusInfo?.allowedTransitions);

    // Check if transition is allowed (skip check for now to allow all transitions)
    // if (currentStatusInfo && currentStatusInfo.allowedTransitions && !currentStatusInfo.allowedTransitions.includes(newStatusName)) {
    //   console.log('❌ AgentPanel: Transition not allowed');
    //   alert('Bu statüye geçiş yapılamaz');
    //   return;
    // }

    // Check if reason is required
    if (newStatus.requiresReason) {
      const reason = prompt('Statü değişikliği için sebep girin:');
      if (!reason) return;
    }

    try {
      // ✨ Önceki statüyü sakla (eğer isteniyorsa ve henüz call-related status değilse)
      if (savePrevious && currentStatusInfo && currentStatusInfo.name !== newStatusName) {
        // Sadece call-related olmayan bir statüden geliyorsak sakla
        const callRelatedStatuses = ['Aramada', 'dialing', 'Arama', 'ACV', 'After Call Work', 'aftercallwork', 'Arama Sonrası'];
        if (!callRelatedStatuses.includes(currentStatusInfo.name)) {
          setPreviousStatusName(currentStatusInfo.name);
          setPreviousStatusStartTime(statusStartTime);
          console.log('💾 Previous status saved:', currentStatusInfo.name, 'Start time:', statusStartTime);
        } else {
          console.log('⏭️ Skipping save - already in call-related status:', currentStatusInfo.name);
        }
      }
      
      // Update status via API - include extension for Asterisk queue management
      const success = await agentStatusContext.setAgentStatus(
        currentUser._id, 
        newStatus._id, 
        undefined, // reason
        agentExtension // extension for Asterisk
      );
      
      if (success) {
        setAgentStatus(newStatusName);
        setCurrentStatusInfo(newStatus);
        // ✨ Reset duration and start time when status changes
        const newStartTime = new Date();
        setStatusStartTime(newStartTime);
        setStatusDuration('00:00:00');
        
        console.log('✅ Agent status changed to:', newStatusName);
        
        // canReceiveCalls might be undefined in old statuses, default to true
        const canReceiveCalls = newStatus.canReceiveCalls !== undefined ? newStatus.canReceiveCalls : true;
        console.log('📞 Asterisk notified:', canReceiveCalls ? 'Queue unpaused (ready for calls)' : 'Queue paused (not receiving calls)');
      } else {
        alert('Statü değiştirilemedi');
      }
    } catch (error) {
      console.error('❌ Status change error:', error);
      alert('Statü değiştirilirken hata oluştu');
    }
  };

  // ✨ Arama başladığında "Aramada" statüsüne geç
  const setCallingStatus = async () => {
    try {
      // Önce "Aramada" statüsünü ara
      let callingStatus = agentStatusContext.getStatusByName('Aramada');
      
      // Eğer "Aramada" bulunamazsa, alternatif isimleri dene
      if (!callingStatus) {
        callingStatus = agentStatusContext.getStatusByName('dialing');
      }
      if (!callingStatus) {
        callingStatus = agentStatusContext.getStatusByName('Arama');
      }
      
      if (callingStatus) {
        console.log('✅ Calling status found:', callingStatus.name);
        await changeAgentStatus(callingStatus.name, true); // Önceki statüyü sakla
      } else {
        console.warn('⚠️ "Aramada" status not found. Available statuses:', agentStatusContext.statuses.map(s => s.name));
      }
    } catch (error) {
      console.error('❌ Error setting calling status:', error);
    }
  };

  // ✨ Arama kapandığında "ACV" statüsüne geç
  const setACVStatus = async () => {
    try {
      // Önce "ACV" statüsünü ara
      let acvStatus = agentStatusContext.getStatusByName('ACV');
      
      // Eğer "ACV" bulunamazsa, alternatif isimleri dene
      if (!acvStatus) {
        acvStatus = agentStatusContext.getStatusByName('After Call Work');
      }
      if (!acvStatus) {
        acvStatus = agentStatusContext.getStatusByName('aftercallwork');
      }
      if (!acvStatus) {
        acvStatus = agentStatusContext.getStatusByName('Arama Sonrası');
      }
      
      if (acvStatus) {
        console.log('✅ ACV status found:', acvStatus.name);
        await changeAgentStatus(acvStatus.name, false); // Önceki statü zaten saklı
      } else {
        console.warn('⚠️ "ACV" status not found. Restoring to previous or default available status.');
        // ACV statüsü bulunamazsa direkt önceki statüye geri dön
        await restorePreviousStatus();
      }
    } catch (error) {
      console.error('❌ Error setting ACV status:', error);
      // Hata durumunda da önceki statüye geri dön
      await restorePreviousStatus();
    }
  };

  // ✨ Müşteri kartı kapatıldığında önceki statüye geri dön
  const restorePreviousStatus = async () => {
    console.log('🔄 restorePreviousStatus called - previousStatusName:', previousStatusName);
    
    // Eğer önceki statü "Aramada" veya ACV ise, varsayılan "available" statüye geç
    const invalidPreviousStatuses = ['Aramada', 'dialing', 'Arama', 'ACV', 'After Call Work', 'aftercallwork', 'Arama Sonrası'];
    const shouldUseDefault = !previousStatusName || invalidPreviousStatuses.includes(previousStatusName);
    
    if (shouldUseDefault) {
      console.log('⚠️ Previous status is invalid or empty, finding default available status');
      
      // İlk "available" statüyü bul
      const availableStatuses = agentStatusContext.statuses.filter((s: any) => s.type === 'available');
      const defaultStatus = availableStatuses.find((s: any) => 
        s.name === 'online' || s.name === 'Online' || s.name === 'Müsait' || s.name === 'Available'
      ) || availableStatuses[0];
      
      if (defaultStatus) {
        console.log('✅ Using default status:', defaultStatus.name);
        const success = await agentStatusContext.setAgentStatus(currentUser._id, defaultStatus._id);
        
        if (success) {
          setAgentStatus(defaultStatus.name);
          setCurrentStatusInfo(defaultStatus);
          const newStartTime = new Date();
          setStatusStartTime(newStartTime);
          setStatusDuration('00:00:00');
          
          // Önceki statü bilgilerini temizle
          setPreviousStatusName(null);
          setPreviousStatusStartTime(null);
          
          console.log('✅ Default status set:', defaultStatus.name);
        } else {
          console.error('❌ Failed to set default status');
        }
      } else {
        console.error('❌ No available status found!');
      }
      return;
    }
    
    // Normal durum: önceki statüye geri dön
    const previousStatus = agentStatusContext.getStatusByName(previousStatusName);
    if (previousStatus) {
      console.log('🔄 Restoring previous status:', previousStatusName);
      
      // Önceki statüyü API'ye gönder
      const success = await agentStatusContext.setAgentStatus(currentUser._id, previousStatus._id);
      
      if (success) {
        setAgentStatus(previousStatusName);
        setCurrentStatusInfo(previousStatus);
        
        // ✨ Önceki statünün başlangıç zamanını geri yükle
        if (previousStatusStartTime) {
          setStatusStartTime(previousStatusStartTime);
          
          // Geçen süreyi hesapla
          const now = new Date();
          const elapsedMs = now.getTime() - previousStatusStartTime.getTime();
          const elapsedSeconds = Math.floor(elapsedMs / 1000);
          const hours = Math.floor(elapsedSeconds / 3600);
          const minutes = Math.floor((elapsedSeconds % 3600) / 60);
          const seconds = elapsedSeconds % 60;
          const formattedDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          setStatusDuration(formattedDuration);
          
          console.log('✅ Previous status restored:', previousStatusName, 'Duration:', formattedDuration);
        } else {
          // Eğer önceki başlangıç zamanı yoksa, şimdiki zamanı kullan
          const newStartTime = new Date();
          setStatusStartTime(newStartTime);
          setStatusDuration('00:00:00');
        }
        
        // Önceki statü bilgilerini temizle
        setPreviousStatusName(null);
        setPreviousStatusStartTime(null);
      } else {
        console.error('❌ Failed to restore previous status');
      }
    } else {
      console.warn('⚠️ Previous status not found:', previousStatusName, '- using default available status');
      
      // Önceki statü bulunamadı, varsayılan available statüye geç
      const availableStatuses = agentStatusContext.statuses.filter((s: any) => s.type === 'available');
      const defaultStatus = availableStatuses.find((s: any) => 
        s.name === 'online' || s.name === 'Online' || s.name === 'Müsait' || s.name === 'Available'
      ) || availableStatuses[0];
      
      if (defaultStatus) {
        const success = await agentStatusContext.setAgentStatus(currentUser._id, defaultStatus._id);
        if (success) {
          setAgentStatus(defaultStatus.name);
          setCurrentStatusInfo(defaultStatus);
          const newStartTime = new Date();
          setStatusStartTime(newStartTime);
          setStatusDuration('00:00:00');
          setPreviousStatusName(null);
          setPreviousStatusStartTime(null);
        }
      }
    }
  };


  // ✨ ACV durumunda müşteri kartı kapalıysa otomatik Hazır'a geç (3 saniyede bir kontrol)
  useEffect(() => {
    const acvStatuses = ['ACV', 'After Call Work', 'aftercallwork', 'Arama Sonrası'];
    const isInACV = currentStatusInfo && acvStatuses.includes(currentStatusInfo.name);
    
    // Eğer ACV'de değilse interval'i başlatma
    if (!isInACV) {
      return;
    }
    
    console.log('🔍 ACV Check: Starting 3-second interval check...');
    
    const checkInterval = setInterval(() => {
      // Müşteri kartı kapalıysa ve hala ACV'deyse Hazır'a geç
      if (!showCustomerCard && !isPendingFinishCode) {
        console.log('✅ ACV Check: Customer card closed, switching to Ready...');
        
        // Hazır statüsünü bul
        const availableStatuses = agentStatusContext.statuses.filter((s: any) => s.type === 'available');
        const readyStatus = availableStatuses.find((s: any) => 
          s.name === 'Hazır' || s.name === 'Ready' || s.name === 'online'
        ) || availableStatuses[0];
        
        if (readyStatus) {
          console.log('✅ ACV Check: Found ready status:', readyStatus.name);
          
          // Status değişikliğini yap
          agentStatusContext.setAgentStatus(currentUser._id, readyStatus._id).then((success: boolean) => {
            if (success) {
              setAgentStatus(readyStatus.name);
              setCurrentStatusInfo(readyStatus);
              const newStartTime = new Date();
              setStatusStartTime(newStartTime);
              setStatusDuration('00:00:00');
              setPreviousStatusName(null);
              setPreviousStatusStartTime(null);
              console.log('✅ ACV Check: Automatically switched to Ready');
            }
          });
        }
        
        // Interval'i durdur
        clearInterval(checkInterval);
      } else {
        console.log('�� ACV Check: Customer card still open or pending finish code, waiting...');
      }
    }, 3000);
    
    // Cleanup
    return () => {
      console.log('🔍 ACV Check: Cleaning up interval');
      clearInterval(checkInterval);
    };
  }, [currentStatusInfo, showCustomerCard, isPendingFinishCode, agentStatusContext.statuses, currentUser?._id]);

  // ✨ Messaging functions
  const loadUsers = async () => {
    try {
      const response = await messagesAPI.getUsers();
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await messagesAPI.getConversations();
      if (response.data.success) {
        console.log('📥 Loaded conversations:', response.data.data);
        setConversations(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading conversations:', error);
    }
  };

  const loadConversation = async (userId: string) => {
    try {
      setLoadingMessages(true);
      const response = await messagesAPI.getConversation(userId);
      if (response.data.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading conversation:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await messagesAPI.getUnreadCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.count);
      }
    } catch (error) {
      console.error('❌ Error loading unread count:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    try {
      const messageContent = newMessage.trim();
      setNewMessage('');

      // Stop typing indicator
      if (socket) {
        socket.emit('stop-typing', { senderId: currentUser?._id, receiverId: selectedUser._id });
      }

      // Send to backend
      const response = await messagesAPI.send(selectedUser._id, messageContent);
      
      if (response.data.success) {
        const sentMessage = response.data.data;
        
        // Add to local messages immediately
        setMessages(prev => [...prev, sentMessage]);
        
        // Emit to socket for real-time delivery
        if (socket) {
          socket.emit('send-message', {
            senderId: currentUser?._id,
            receiverId: selectedUser._id,
            content: messageContent,
            messageId: sentMessage._id
          });
        }
        
        // Reload conversations to update last message
        loadConversations();
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setSnackbarMessage('Mesaj gönderilemedi');
      setSnackbarOpen(true);
    }
  };

  // Status duration timer - calculates from statusStartTime
  useEffect(() => {
    if (!statusStartTime) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      const elapsedMs = now.getTime() - statusStartTime.getTime();
      const elapsedSeconds = Math.floor(elapsedMs / 1000);
      const hours = Math.floor(elapsedSeconds / 3600);
      const minutes = Math.floor((elapsedSeconds % 3600) / 60);
      const seconds = elapsedSeconds % 60;
      const formattedDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setStatusDuration(formattedDuration);
    }, 1000);

    return () => clearInterval(interval);
  }, [statusStartTime]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showStatusDropdown && !(event.target as Element).closest('.status-card')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const tabs = [
    'Aktif Çağrı',
    'Mesajlarım', 
    'Takım',
    'Performans',
    'Önceki Çağrılar',
    'Kuyruklar',
    'Çağrı Değerlendirmeleri',
   
  ];

  return (
    <div className="app-container" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>
      {/* Incoming Call Dialog */}
      <Dialog 
        open={showCallDialog} 
        onClose={() => {
          stopRingtone();
          setShowCallDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: '#1e3a8a',
            color: 'white',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#f59e0b', width: 56, height: 56 }}>
              <PhoneIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Gelen Çağrı
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {incomingCall?.queueName ? `Kuyruk: ${incomingCall.queueName}` : 'Agent Panel'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#f59e0b' }}>
              {incomingCall?.callerNumber || 'Unknown'}
            </Typography>
            
            {incomingCall?.queueName && (
              <Box sx={{ 
                bgcolor: 'rgba(59, 130, 246, 0.15)', 
                borderRadius: 2, 
                p: 2,
                mt: 2,
                mb: 2,
                border: '2px solid rgba(59, 130, 246, 0.4)'
              }}>
                <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.75rem', mb: 0.5 }}>
                  KUYRUK
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
                  {incomingCall.queueName}
                </Typography>
              </Box>
            )}
            
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Numara: {incomingCall?.callerNumber || 'Unknown'}
            </Typography>
          </Box>

          <Box sx={{ 
            bgcolor: 'rgba(245, 158, 11, 0.1)', 
            borderRadius: 2, 
            p: 2, 
            border: '1px solid rgba(245, 158, 11, 0.3)'
          }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Çağrıyı kabul etmek için "Kabul Et" butonuna tıklayın
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button 
            onClick={handleRejectCall}
            variant="outlined"
            sx={{ 
              borderColor: '#ef4444',
              color: '#ef4444',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                borderColor: '#ef4444',
                bgcolor: 'rgba(239, 68, 68, 0.1)'
              },
              minWidth: 120
            }}
          >
            Reddet
          </Button>
          <Button 
            onClick={handleAnswerCall}
            variant="contained"
            sx={{ 
              bgcolor: '#f59e0b',
              color: 'white',
              borderWidth: 2,
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
              '&:hover': {
                bgcolor: '#d97706',
                boxShadow: '0 6px 16px rgba(245, 158, 11, 0.6)',
              },
              minWidth: 120
            }}
          >
            'Kabul Et'
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden audio elements for WebRTC streams */}
      <audio 
        id="localAudio" 
        autoPlay 
        muted 
        style={{ display: 'none' }} 
        onLoadedMetadata={() => console.log('🎤 Local audio loaded')}
        onError={(e) => console.error('❌ Local audio error:', e)}
        onPlay={() => console.log('▶️ Local audio started playing')}
        onPause={() => console.log('⏸️ Local audio paused')}
      />
      <audio 
        id="remoteAudio" 
        autoPlay 
        playsInline
        style={{ display: 'none' }} 
        onLoadedMetadata={() => console.log('🔊 Remote audio loaded')}
        onError={(e) => console.error('❌ Remote audio error:', e)}
        onPlay={() => console.log('▶️ Remote audio started playing')}
        onPause={() => console.log('⏸️ Remote audio paused')}
        onCanPlay={() => console.log('🎵 Remote audio can play')}
        onLoadStart={() => console.log('🔄 Remote audio load started')}
        onVolumeChange={(e) => console.log('🔊 Volume:', (e.target as HTMLAudioElement).volume)}
      />
      
      {/* Additional audio elements from agent-p */}
      <audio autoPlay></audio>
      <audio autoPlay></audio>
      
      {/* Incoming Call Modal (legacy overlay removed in favor of MUI Dialog) */}

      {/* Left Sidebar */}
      <div className="sidebar">
        <div className="sidebar-logo">
        </div>
        <div className="sidebar-menu">
          <div className="sidebar-item active">
            <div className="sidebar-icon">
              <PersonIcon />
            </div>
            <div className="sidebar-label">Müşteri<br/>Temsilcisi</div>
          </div>
          
          <div className="sidebar-item">
            <div className="sidebar-icon">
              <HomeIcon />
            </div>
            <div className="sidebar-label">Tüm<br/>Görevler</div>
          </div>
          
          <div className="sidebar-item">
            <div className="sidebar-icon">
              <CallIcon />
            </div>
            <div className="sidebar-label">Çağrı</div>
          </div>
        </div>
        
        <div 
          className="sidebar-profile"
          onClick={() => setShowProfileMenu(!showProfileMenu)}
        >
          <div className="profile-avatar">
            {currentUser?.firstName?.charAt(0) || 'M'}{currentUser?.lastName?.charAt(0) || 'K'}
          </div>
          <div className="profile-name">
            {currentUser?.firstName || 'Melek'}<br/>{currentUser?.lastName || 'Korkmaz'}
          </div>
          
          {/* Profile Menu */}
          {showProfileMenu && (
            <div className="profile-menu">
              <div 
                className="profile-menu-item"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/');
                  setShowProfileMenu(false);
                }}
              >
                <HomeIcon className="profile-menu-item-icon" />
                <span>Ana Menü</span>
              </div>
              <div 
                className="profile-menu-item logout"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    console.log('🔐 Logout attempt');
                    await authService.logout();
                    console.log('✅ Logout successful');
                    localStorage.clear();
                    window.location.href = '/login';
                  } catch (error) {
                    console.error('❌ Logout error:', error);
                    // Force logout even if API call fails
                    localStorage.clear();
                    window.location.href = '/login';
                  }
                  setShowProfileMenu(false);
                }}
              >
                <ExitToAppIcon className="profile-menu-item-icon" />
                <span>Çıkış Yap</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Header - Modern Call Center Design */}
        <div className="header">
          <div className="header-left">
            <div 
              className="status-card"
              style={{ 
                backgroundColor: currentStatusInfo?.color || '#beb042',
                boxShadow: currentStatusInfo?.color ? `0 2px 8px ${currentStatusInfo.color}30` : undefined
              }}
              onClick={() => {
                console.log('Status dropdown clicked, current state:', showStatusDropdown);
                console.log('Available statuses:', availableStatuses);
                setShowStatusDropdown(!showStatusDropdown);
              }}
            >
              <div className="status-content">
                <div className="status-title">{currentStatusInfo?.name || 'Back Office'}</div>
                <div className="status-duration">{statusDuration}</div>
              </div>
              <div className={`status-dropdown-icon ${showStatusDropdown ? 'opened' : ''}`}>
                <span className="line1"></span>
                <span className="line2"></span>
              </div>
              
              {/* Custom Status Dropdown */}
              {showStatusDropdown && (
                <div className="status-dropdown-menu">
                  {availableStatuses.map((status) => (
                    <div
                      key={status._id}
                      className={`status-option ${status._id === currentStatusInfo?._id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        changeAgentStatus(status.name);
                        setShowStatusDropdown(false);
                      }}
                    >
                      <div 
                        className="status-color-dot" 
                        style={{ backgroundColor: status.color }}
                      ></div>
                      <div className="status-option-content">
                        <div className="status-option-name">{status.name}</div>
                        {status.description && (
                          <div className="status-option-desc">{status.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="header-center">
            <div className="phone-input-container">
              <input 
                type="tel"
                name="phoneNumber"
                className="phone-input"
                placeholder="Bir telefon numarası yazın"
                value={phoneNumber}
                autoComplete="tel"
                onChange={(e) => {
                  // ✨ Boşlukları ve özel karakterleri temizle
                  const cleaned = e.target.value.replace(/[\s\-\(\)]/g, '');
                  setPhoneNumber(cleaned);
                }}
                onKeyDown={(e) => {
                  // ✨ Enter tuşuna basıldığında arama yap
                  if (e.key === 'Enter' && phoneNumber.trim() && selectedQueue) {
                    e.preventDefault();
                    makeCall();
                  }
                }}
              />
              <button
              className="dialpad-button"
              title="Dialpad"
              onClick={() => setShowDialpad(true)}
              >
              ⚏
              </button>
            </div>
          
            <select
              className="queue-dropdown"
              value={selectedQueue}
              onChange={(e) => {
                const newQueue = e.target.value;
                setSelectedQueue(newQueue);
                console.log('📋 Queue selected for UI filtering:', newQueue);

                // ✨ Notify backend via WebSocket
                if (socket && currentUser) {
                  socket.emit('agent-queue-selected', {
                    agentId: currentUser._id,
                    queueId: newQueue
                  });
                  console.log('📡 Sent agent-queue-selected to backend:', { agentId: currentUser._id, queueId: newQueue });
                }
              }}
            >
              <option value="">Kuyruk Seçiniz</option>
              {queueContext.queues.map(queue => (
                <option key={queue._id} value={queue._id}>
                  {queue.name}
                </option>
              ))}
            </select>
            
            </div>
            <div className='header-right'>
            <div className="call-controls">
              <button 
                className={`call-button ${isCallActive ? 'active' : ''}`}
                onClick={toggleMute}
                disabled={!isCallActive}
                title="Mikrofon"
                style={isMuted ? { backgroundColor: '#ef4444', color: 'white' } : {}}
              >
                {isMuted ? <MicOffIconSolid /> : <MicIconSolid />}
              </button>
              
              <button 
                className={`call-button ${isCallActive ? 'active' : ''}`}
                disabled={!isCallActive}
                title="Transfer"
              >
                <TransferIcon />
              </button>

              {/* ✨ Konferans Butonu */}
              {lines[1].isActive && lines[2].isActive && (
                <button 
                  className="call-button active"
                  onClick={mergeCalls}
                  title="Konferans (Birleştir)"
                  style={{ backgroundColor: '#7c3aed', color: 'white' }}
                >
                  <PeopleIcon />
                </button>
              )}
              
              {isCallActive && (
                <>
                  <button 
                    className={`call-button ${isOnHold ? 'active' : ''}`}
                    onClick={toggleHold}
                    title={isOnHold ? "Devam Et" : "Beklet"}
                  >
                    {isOnHold ? <PlayIcon /> : <PauseIcon />}
                  </button>
                  <button className="call-button hangup" onClick={hangupCall} title="Çağrıyı Sonlandır">
                    <CallEndIcon />
                  </button>
                </>
              )}
              
              <button 
                className={`call-button ${isCallActive ? 'active' : ''}`}
                onClick={() => makeCall()}
                disabled={!phoneNumber || !selectedQueue || isPendingFinishCode && showCustomerCard}
                title={isPendingFinishCode ? "Önce mevcut çağrı için finish code seçin" : "Ara"}
              >
                <PhoneIcon />
              </button>
            </div>
            </div>
        </div>

        {/* Content Layout - 2 Columns */}
        <div className="content-layout">
          {/* Left Column - Active Tasks */}
          <div className="left-column">
            <div className="tasks-header">
              <div className="tasks-title">Aktif Görevler</div>
              
              <div className="task-category">
                <div className="category-title">Çağrı</div>
             
              </div>
            </div>
            
            <div className="task-list">
              {/* 1. Hat */}
              <div 
                className={`task-item ${activeLine === 1 ? 'active' : ''}`}
                onClick={() => setActiveLine(1)}
                style={{ cursor: 'pointer' }}
              > 
                <div className="task-icon">
                  <CallIcon />
                </div>
                <div className="task-content">
                  <div className="task-line">1. Hat</div>
                  {lines[1].isActive && (
                    <>
                      <div className="task-phone">{lines[1].currentCallPhoneNumber || lines[1].phoneNumber}</div>
                      <div className="task-duration">{formatDuration(lines[1].callDuration)}</div>
                    </>
                  )}
                </div>
              </div>
              
              {/* 2. Hat */}
              <div 
                className={`task-item ${activeLine === 2 ? 'active' : ''}`}
                onClick={() => setActiveLine(2)}
                style={{ cursor: 'pointer' }}
              >
                <div className="task-icon">
                  <CallIcon />
                </div>
                <div className="task-content">
                  <div className="task-line">2. Hat</div>
                  {lines[2].isActive && (
                    <>
                      <div className="task-phone">{lines[2].currentCallPhoneNumber || lines[2].phoneNumber}</div>
                      <div className="task-duration">{formatDuration(lines[2].callDuration)}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* User Profile Section */}
            <div className="user-profile">
             
              
              {/* Status Counters */}
              <div className="status-counters">
                <div className="status-counter">
                  <div className="counter-value">1</div>
                  <div className="counter-label">
                    <div className="counter-dot ready"></div>
                    Hazır
                  </div>
                </div>
                
                <div className="status-counter">
                  <div className="counter-value">0</div>
                  <div className="counter-label">
                    <div className="counter-dot talking"></div>
                    Konuşuyor
                  </div>
                </div>
                
                <div className="status-counter">
                  <div className="counter-value">0</div>
                  <div className="counter-label">
                    <div className="counter-dot waiting"></div>
                    Bekliyor
                  </div>
                </div>
                
                <div className="status-counter">
                  <div className="counter-value">0</div>
                  <div className="counter-label">
                    <div className="counter-dot break"></div>
                    Mola
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Main Content */}
          <div className="right-column">
            {/* Tab Navigation */}
            <div className="tab-navigation">
              {tabs.map((tab) => (
                <div 
                  key={tab}
                  className={`tab-item ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </div>
              ))}
            </div>
            
            {/* Tab Content */}
            <div className="tab-content">
              {isCallActive ? (
                <div className="active-call">
                  <div className="call-info">
                    <div className="call-number">{phoneNumber}</div>
                    <div className="call-duration">{formatDuration(callDuration)}</div>
                  </div>
                  
                  {/* Müşteri Kartı - Scroll edilebilir bölüm */}
                  <div className="customer-card-container">
                    <CustomerCard 
                      phoneNumber={currentCallPhoneNumber}
                      callId={currentCallId}
                      isCallActive={isCallActive}
                      showCustomerCard={showCustomerCard}
                      requireFinishCode={!isCallActive && !!currentCallPhoneNumber}
                      onFinishCodeSelected={handleFinishCodeSelected}
                      externalData={currentCustomerCardData}
                      queueData={currentQueueData}
                      onFinish={async () => {
                        console.log('✅ Call resolved by user');
                        setIsCallActive(false);
                        setCallDuration(0);
                        setSoftphoneStatus('bağlı');
                        setShowCustomerCard(true);
                        setACVStatus();
                      }}
                      onClose={async () => {
                        console.log('✅ Customer card closed by user');
                        setShowCustomerCard(false);
                        setCurrentCustomerCardData(null);
                        setCurrentQueueData(null);
                        await restorePreviousStatus();
                      }}
                    />
                  </div>
                  
                  {/* Customer card controls removed - now in header */}
                </div>
              ) : showCustomerCard && currentCallPhoneNumber ? (
                <div className="active-call">
                  <div className="call-info">
                    <div className="call-number">{currentCallPhoneNumber}</div>
                    <div className="call-duration">Arama Kapandı</div>
                  </div>
                  
                  {/* Müşteri Kartı - Arama kapandıktan sonra da görünür */}
                  <div className="customer-card-container">
                    <CustomerCard 
                      phoneNumber={currentCallPhoneNumber}
                      callId={currentCallId}
                      isCallActive={false}
                      showCustomerCard={showCustomerCard}
                      requireFinishCode={true}
                      onFinishCodeSelected={handleFinishCodeSelected}
                      externalData={currentCustomerCardData}
                      queueData={currentQueueData}
                      onFinish={async () => {
                        console.log('✅ Call resolved by user');
                        setCurrentCallPhoneNumber('');
                        setShowCustomerCard(false);
                        setSoftphoneStatus('bağlı');
                        setCurrentCustomerCardData(null);
                        setCurrentQueueData(null);
                        await restorePreviousStatus();
                      }}
                      onClose={async () => {
                        console.log('✅ Customer card closed by user');
                        setShowCustomerCard(false);
                        setCurrentCallPhoneNumber('');
                        setCurrentCustomerCardData(null);
                        setCurrentQueueData(null);
                        await restorePreviousStatus();
                      }}
                    />
                  </div>
                </div>
              ) : activeTab === 'Önceki Çağrılar' ? (
                <div className="previous-calls-tab">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      Önceki Çağrılar
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Numara ara..."
                      value={previousCallsSearch}
                      onChange={(e) => setPreviousCallsSearch(e.target.value)}
                      sx={{ width: 250 }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, color: '#94a3b8' }}>🔍</Box>
                        )
                      }}
                    />
                  </Box>
                  {loadingPreviousCalls ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <CircularProgress />
                    </Box>
                  ) : previousCalls.length === 0 ? (
                    <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <CardContent>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <PhoneMissedIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                            Önceki Çağrı Bulunamadı
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            Henüz hiçbir çağrı gerçekleştirmemişsiniz
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Çağrı ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Yön</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Arayan</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Aranan</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Başlangıç</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Bitiş</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Süre</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Durum</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Kuyruk</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>İşlem</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previousCalls
                            .filter(call => {
                              if (!previousCallsSearch.trim()) return true;
                              const search = previousCallsSearch.toLowerCase();
                              return (
                                (call.callerNumber || '').toLowerCase().includes(search) ||
                                (call.calledNumber || '').toLowerCase().includes(search)
                              );
                            })
                            .map((call, index) => (
                            <TableRow
                              key={call._id}
                              sx={{
                                bgcolor: index % 2 === 0 ? '#ffffff' : '#f0f4f8',
                                '&:hover': { bgcolor: '#e0e7ef' },
                                '&:last-child td, &:last-child th': { border: 0 }
                              }}
                            >
                              <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                {call._id?.substring(0, 8)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={call.direction === 'incoming' ? 'Gelen' : 'Giden'}
                                  size="small"
                                  sx={{
                                    bgcolor: call.direction === 'incoming' ? '#3b82f6' : '#8b5cf6',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>{call.callerNumber || '-'}</TableCell>
                              <TableCell>{call.calledNumber || '-'}</TableCell>
                              <TableCell>{call.startTime ? new Date(call.startTime).toLocaleString('tr-TR') : '-'}</TableCell>
                              <TableCell>{call.endTime ? new Date(call.endTime).toLocaleString('tr-TR') : '-'}</TableCell>
                              <TableCell>{call.duration ? `${call.duration}s` : '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    call.status === 'ended' ? 'Tamamlandı' :
                                    call.status === 'missed' ? 'Cevapsız' :
                                    call.status === 'answered' ? 'Cevaplandı' :
                                    call.status === 'failed' ? 'Başarısız' :
                                    call.status === 'ringing' ? 'Çalıyor' :
                                    call.status === 'busy' ? 'Meşgul' :
                                    call.status === 'unanswered' ? 'Cevapsız' :
                                    call.status === 'transferred' ? 'Transfer Edildi' :
                                    call.status === 'completed' ? 'Tamamlandı' :
                                    call.status === 'abandoned' ? 'Terk Edildi' :
                                    call.status === 'no-answer' ? 'Cevapsız' :
                                    call.status
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      call.status === 'ended' || call.status === 'answered' || call.status === 'completed' ? '#10b981' :
                                      call.status === 'missed' || call.status === 'failed' || call.status === 'unanswered' || call.status === 'no-answer' || call.status === 'abandoned' ? '#ef4444' :
                                      call.status === 'busy' ? '#f59e0b' :
                                      call.status === 'transferred' ? '#3b82f6' :
                                      call.status === 'ringing' ? '#8b5cf6' :
                                      '#94a3b8',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>{call.queueId?.name || '-'}</TableCell>
                              <TableCell>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<PhoneIcon />}
                                  onClick={() => {
                                    // Aranacak numarayı belirle (gelen çağrıysa arayan, giden çağrıysa aranan)
                                    let targetNumber = call.direction === 'incoming' 
                                      ? call.callerNumber 
                                      : call.calledNumber;
                                    
                                    if (targetNumber) {
                                      // Başında 0 yoksa ekle
                                      if (!targetNumber.startsWith('0') && targetNumber.length === 10) {
                                        targetNumber = '0' + targetNumber;
                                      }
                                      
                                      // Numarayı input'a da set et (görsel için)
                                      setPhoneNumber(targetNumber);
                                      setActiveTab('Arama');
                                      
                                      // Doğrudan aramayı yap (parametre olarak gönder)
                                      setTimeout(() => {
                                        makeCall(targetNumber);
                                      }, 100);
                                    } else {
                                      alert('Telefon numarası bulunamadı');
                                    }
                                  }}
                                  sx={{
                                    bgcolor: '#10b981',
                                    '&:hover': { bgcolor: '#059669' },
                                    textTransform: 'none',
                                    fontWeight: 500
                                  }}
                                >
                                  Ara
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </div>
              ) : activeTab === 'Performans' ? (
                <div className="performance-tab">
                  <Typography variant="h5" sx={{ mb: 4, fontWeight: 600, color: '#1e293b' }}>
                    Performans İstatistikleri
                  </Typography>
                  {loadingPerformance ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Call Statistics Section */}
                      <Box>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center' }}>
                          <AssessmentIcon sx={{ mr: 1, color: '#3b82f6' }} />
                          Çağrı İstatistikleri
                        </Typography>

                        {/* Çağrı Sayıları */}
                        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                          <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'inline-block', mb: 3 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: '#152b69',
                                  fontSize: '1.2rem',
                                  border: '1px solid #152b69',
                                  borderRadius: 1,
                                  px: 2,
                                  py: 1
                                }}
                              >
                                Çağrı Sayıları
                              </Typography>
                            </Box>
                            <Grid container spacing={3}>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <PhoneIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.totalCalls}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Toplam Çağrı Adedi
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <PhoneCallbackIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.incomingCalls}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Gelen Çağrı Adedi
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <PhoneInTalkIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.outgoingCalls}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Giden Çağrı Adedi
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Konuşma Süreleri */}
                        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                          <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'inline-block', mb: 3 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: '#152b69',
                                  fontSize: '1.2rem',
                                  border: '1px solid #152b69',
                                  borderRadius: 1,
                                  px: 2,
                                  py: 1
                                }}
                              >
                                Toplam Konuşma Süreleri
                              </Typography>
                            </Box>
                            <Grid container spacing={3}>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <TimerIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.totalCallDuration}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Toplam Konuşma Süresi
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <TimerIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.incomingCallDuration}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Gelen Çağrı Süresi
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <TimerIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.outgoingCallDuration}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Giden Çağrı Süresi
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Ortalama Çağrı Süreleri */}
                        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                          <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'inline-block', mb: 3 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: '#152b69',
                                  fontSize: '1.2rem',
                                  border: '1px solid #152b69',
                                  borderRadius: 1,
                                  px: 2,
                                  py: 1
                                }}
                              >
                                Ortalama Çağrı Süreleri
                              </Typography>
                            </Box>
                            <Grid container spacing={3}>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <AssessmentIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.avgTotalCallDuration}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Toplam Çağrı Ortalaması
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <AssessmentIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.avgIncomingCallDuration}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Gelen Çağrı Ortalaması
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 4,
                                  bgcolor: '#152b6915',
                                  borderRadius: 2,
                                  border: '2px solid #152b69',
                                  minHeight: '160px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                  }
                                }}>
                                  <AssessmentIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                    {performanceData.avgOutgoingCallDuration}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                    Giden Çağrı Ortalaması
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Box>

                      {/* Status Durations Section */}
                      <Box mt={4}>
                        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                          <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'inline-block', mb: 3 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: '#152b69',
                                  fontSize: '1.2rem',
                                  border: '1px solid #152b69',
                                  borderRadius: 1,
                                  px: 2,
                                  py: 1
                                }}
                              >
                                Bugün Statü Süreleri
                              </Typography>
                            </Box>
                            <Grid container spacing={3}>
                              {statusDurations.length > 0 ? (
                                statusDurations.map((status, index) => (
                                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                    <Box
                                      sx={{
                                        textAlign: 'center',
                                        p: 4,
                                        bgcolor: '#152b6915',
                                        borderRadius: 2,
                                        border: '2px solid #152b69',
                                        minHeight: '160px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-4px)',
                                          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                        }
                                      }}
                                    >
                                      {status.isCurrent && (
                                        <Chip
                                          label="Aktif"
                                          size="small"
                                          sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            bgcolor: '#10b981',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      )}
                                      <TimerIcon sx={{ fontSize: 48, color: '#152b69', mb: 2 }} />
                                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#152b69', mb: 1.5, fontSize: '2.5rem' }}>
                                        {status.formattedDuration}
                                      </Typography>
                                      <Typography variant="h6" sx={{ color: '#152b69', fontWeight: 500, fontSize: '1rem' }}>
                                        {status.statusName}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#152b69', mt: 1, fontSize: '0.85rem' }}>
                                        {status.count} değişiklik
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))
                              ) : (
                                <Grid size={{ xs: 12 }}>
                                  <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                                      Statü süresi verisi bulunamadı
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Box>
                    </>
                  )}
                </div>
              ) : activeTab === 'Mesajlarım' ? (
                <div className="messages-tab">
                  <Box sx={{ height: '600px', display: 'flex', border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                    {/* Users List */}
                    <Box sx={{ width: '300px', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                          Mesajlarım
                          {unreadCount > 0 && (
                            <Chip 
                              label={unreadCount} 
                              size="small" 
                              sx={{ ml: 1, bgcolor: '#ef4444', color: 'white', fontWeight: 600 }}
                            />
                          )}
                        </Typography>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() => setShowUserSelectDialog(true)}
                          sx={{
                            bgcolor: '#3b82f6',
                            '&:hover': { bgcolor: '#2563eb' },
                            textTransform: 'none'
                          }}
                        >
                          + Yeni Mesaj
                        </Button>
                      </Box>
                      <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {conversations.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                            <EmailIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                              Henüz konuşma yok
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setShowUserSelectDialog(true)}
                              sx={{ textTransform: 'none' }}
                            >
                              İlk Mesajı Gönder
                            </Button>
                          </Box>
                        ) : (
                          conversations.map((conv: any) => {
                            // For agent: _id is the other user (already populated)
                            // For lastMessage fallback: check who is the other party
                            let otherUser: any;
                            if (conv._id && typeof conv._id === 'object' && conv._id.firstName) {
                              // _id is populated user object (agent conversations)
                              otherUser = conv._id;
                            } else if (conv.lastMessage) {
                              // Fallback: determine from lastMessage
                              otherUser = conv.lastMessage.senderId?._id === currentUser?._id 
                                ? conv.lastMessage.receiverId 
                                : conv.lastMessage.senderId;
                            }
                            
                            const displayName = otherUser?.firstName && otherUser?.lastName 
                              ? `${otherUser.firstName} ${otherUser.lastName}` 
                              : otherUser?.email || 'Bilinmeyen Kullanıcı';
                            const lastMessagePreview = conv.lastMessage?.content?.substring(0, 50) || '';
                            const isUnread = conv.unreadCount > 0;
                            
                            // Skip if otherUser couldn't be determined
                            if (!otherUser || !otherUser._id) {
                              console.warn('⚠️ Could not determine otherUser for conversation:', conv);
                              return null;
                            }
                            
                            return (
                              <Box
                                key={conv._id?._id || conv._id}
                                onClick={() => {
                                  setSelectedUser(otherUser);
                                  loadConversation(otherUser._id);
                                }}
                                sx={{
                                  p: 2,
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  bgcolor: selectedUser?._id === otherUser?._id ? '#f8fafc' : 'white',
                                  '&:hover': { bgcolor: '#f8fafc' },
                                  transition: 'background-color 0.2s'
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ bgcolor: '#3b82f6', width: 40, height: 40 }}>
                                    {displayName.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body1" sx={{ fontWeight: isUnread ? 700 : 500, color: '#1e293b' }}>
                                      {displayName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {lastMessagePreview}
                                    </Typography>
                                  </Box>
                                  {isUnread && (
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                  )}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>

                    {/* Chat Area */}
                    {selectedUser ? (
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Chat Header */}
                        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: '#3b82f6' }}>
                              {selectedUser.firstName?.charAt(0).toUpperCase() || 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {selectedUser.firstName} {selectedUser.lastName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {selectedUser.role === 'agent' ? 'Temsilci' : selectedUser.role === 'supervisor' ? 'Süpervizör' : 'Kullanıcı'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Messages */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f8fafc' }}>
                          {loadingMessages ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                              <CircularProgress />
                            </Box>
                          ) : messages.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                              <EmailIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                Henüz mesaj yok. İlk mesajı gönderin!
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {messages.map((msg: any) => {
                                const isMine = msg.senderId?._id === currentUser?._id;
                                return (
                                  <Box
                                    key={msg._id}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: isMine ? 'flex-end' : 'flex-start'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        maxWidth: '70%',
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: isMine ? '#3b82f6' : 'white',
                                        color: isMine ? 'white' : '#1e293b',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {msg.content}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                                        {new Date(msg.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                        {isMine && msg.isRead && ' ✓✓'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                              {typingUsers.has(selectedUser._id) && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                      yazıyor...
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* Message Input */}
                        <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: 'white' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Mesajınızı yazın..."
                              value={newMessage}
                              onChange={(e) => {
                                setNewMessage(e.target.value);
                                if (socket && e.target.value) {
                                  socket.emit('typing', { senderId: currentUser?._id, receiverId: selectedUser._id });
                                } else if (socket && !e.target.value) {
                                  socket.emit('stop-typing', { senderId: currentUser?._id, receiverId: selectedUser._id });
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                                  e.preventDefault();
                                  handleSendMessage();
                                }
                              }}
                              multiline
                              maxRows={3}
                            />
                            <Button
                              variant="contained"
                              onClick={handleSendMessage}
                              disabled={!newMessage.trim()}
                              sx={{
                                bgcolor: '#3b82f6',
                                '&:hover': { bgcolor: '#2563eb' },
                                minWidth: '100px'
                              }}
                            >
                              Gönder
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <EmailIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                            Mesajlaşmaya Başlayın
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            Soldaki listeden bir kullanıcı seçin
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* User Selection Dialog */}
                  <Dialog 
                    open={showUserSelectDialog} 
                    onClose={() => setShowUserSelectDialog(false)}
                    maxWidth="xs"
                    fullWidth
                  >
                    <DialogTitle>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Kullanıcı Seç</Typography>
                        <IconButton size="small" onClick={() => setShowUserSelectDialog(false)}>
                          ✕
                        </IconButton>
                      </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                      <List>
                        {users.map((user) => (
                          <ListItem
                            key={user._id}
                            onClick={() => {
                              setSelectedUser(user);
                              loadConversation(user._id);
                              setShowUserSelectDialog(false);
                            }}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#f8fafc' }
                            }}
                          >
                            <Avatar sx={{ bgcolor: '#3b82f6', mr: 2 }}>
                              {(user.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
                            </Avatar>
                            <ListItemText
                              primary={user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                              secondary={user.role}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : activeTab === 'Takım' ? (
                <div className="team-tab">
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
                    Takım
                  </Typography>
                  <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <PeopleIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                          Takım Bilgileri
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          Takımınızla ilgili bilgiler burada görünecek
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              ) : activeTab === 'Kuyruklar' ? (
                <div className="queues-tab">
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
                    Kuyruklar
                  </Typography>
                  <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <QueueIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                          Kuyruk Bilgileri
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          Kuyruklarla ilgili bilgiler burada görünecek
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              ) : activeTab === 'Çağrı Değerlendirmeleri' ? (
                <div className="evaluations-tab">
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
                    Çağrı Değerlendirmeleri
                  </Typography>
                  <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <AssessmentIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                          Çağrı Değerlendirmeleri
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          Çağrı değerlendirme raporları burada görünecek
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    📞
                  </div>
                  <div className="empty-state-title">Aktif Çağrı Yok</div>
                  <div className="empty-state-description">
                    Durumunuzu Görev almaya başlamak için Hazır olarak değiştirin.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Finish code popup kaldırıldı - artık müşteri kartında */}

      {/* ✨ Dialpad Popover - Solda açılsın, daha güzel tasarım */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: showDialpad ? 'block' : 'none',
          zIndex: 999,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(2px)'
        }}
        onClick={() => setShowDialpad(false)}
      />
      
      {showDialpad && (
        <Paper
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '320px',
            p: 3,
            zIndex: 1000,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            animation: 'slideUp 0.3s ease-out'
          }}
        >
          {/* Dialpad Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontWeight: 700, color: '#1e3a8a', fontSize: '18px' }}>
              📞 Dialpad
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowDialpad(false)}
              sx={{ color: '#94a3b8' }}
            >
              ✕
            </IconButton>
          </Box>

          {/* Dialpad Input Display */}
          <TextField
            fullWidth
            value={dialpadInput}
            
            variant="filled"
            placeholder="0"
            sx={{
              mb: 2.5,
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              '& .MuiFilledInput-root': {
                fontSize: '28px',
                fontWeight: 'bold',
                textAlign: 'center',
                letterSpacing: '4px',
                backgroundColor: '#f1f5f9',
                '&:hover': {
                  backgroundColor: '#e2e8f0'
                }
              },
              '& .MuiFilledInput-underline:before': {
                borderBottom: 'none'
              },
              '& .MuiFilledInput-underline:after': {
                borderBottom: 'none'
              }
            }}
          />

          {/* Dialpad Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2.5 }}>
            {/* Row 1: 1 2 3 */}
            {['1', '2', '3'].map((digit) => (
              <Button
                key={digit}
                variant="contained"
                sx={{
                  py: 2,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  backgroundColor: '#1e3a8a',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#152b69',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)'
                  }
                }}
                onClick={() => {
                  const newInput = dialpadInput + digit;
                  setDialpadInput(newInput);
                  if (isCallActive) {
                    freePBXWebRTCService.sendDTMF(digit);
                  }
                }}
              >
                {digit}
              </Button>
            ))}

            {/* Row 2: 4 5 6 */}
            {['4', '5', '6'].map((digit) => (
              <Button
                key={digit}
                variant="contained"
                sx={{
                  py: 2,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  backgroundColor: '#1e3a8a',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#152b69',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)'
                  }
                }}
                onClick={() => {
                  const newInput = dialpadInput + digit;
                  setDialpadInput(newInput);
                  if (isCallActive) {
                    freePBXWebRTCService.sendDTMF(digit);
                  }
                }}
              >
                {digit}
              </Button>
            ))}

            {/* Row 3: 7 8 9 */}
            {['7', '8', '9'].map((digit) => (
              <Button
                key={digit}
                variant="contained"
                sx={{
                  py: 2,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  backgroundColor: '#1e3a8a',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#152b69',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)'
                  }
                }}
                onClick={() => {
                  const newInput = dialpadInput + digit;
                  setDialpadInput(newInput);
                  if (isCallActive) {
                    freePBXWebRTCService.sendDTMF(digit);
                  }
                }}
              >
                {digit}
              </Button>
            ))}

            {/* Row 4: * 0 # */}
            <Button
              variant="contained"
              sx={{
                py: 2,
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#ffffff',
                backgroundColor: '#1e3a8a',
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#152b69',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
              onClick={() => {
                const newInput = dialpadInput + '*';
                setDialpadInput(newInput);
                if (isCallActive) {
                  freePBXWebRTCService.sendDTMF('*');
                }
              }}
            >
              *
            </Button>
            <Button
              variant="contained"
              sx={{
                py: 2,
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#ffffff',
                backgroundColor: '#1e3a8a',
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#152b69',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
              onClick={() => {
                const newInput = dialpadInput + '0';
                setDialpadInput(newInput);
                if (isCallActive) {
                  freePBXWebRTCService.sendDTMF('0');
                }
              }}
            >
              0
            </Button>
            <Button
              variant="contained"
              sx={{
                py: 2,
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#ffffff',
                backgroundColor: '#1e3a8a',
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#152b69',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
              onClick={() => {
                const newInput = dialpadInput + '#';
                setDialpadInput(newInput);
                if (isCallActive) {
                  freePBXWebRTCService.sendDTMF('#');
                }
              }}
            >
              #
            </Button>
          </Box>

          {/* Backspace Button */}
          <Button
            fullWidth
            variant="outlined"
            sx={{
              mb: 1.5,
              py: 1.2,
              color: '#ef4444',
              borderColor: '#fca5a5',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px',
              border: '2px solid',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#fee2e2',
                borderColor: '#ef4444',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => setDialpadInput(dialpadInput.slice(0, -1))}
          >
            ← Sil
          </Button>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setDialpadInput('');
                setShowDialpad(false);
              }}
              sx={{
                py: 1,
                color: '#64748b',
                borderColor: '#cbd5e1',
                fontSize: '13px',
                fontWeight: '600',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  borderColor: '#94a3b8'
                }
              }}
            >
              Kapat
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setPhoneNumber(dialpadInput);
                setDialpadInput('');
                setShowDialpad(false);
              }}
              sx={{
                py: 1,
                backgroundColor: '#10b981',
                fontSize: '13px',
                fontWeight: '600',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#059669',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }
              }}
            >
              ✓ Kullan
            </Button>
          </Box>
        </Paper>
      )}

      {/* ✨ Dialer Disposition Dialog */}
      <Dialog
        open={showDispositionDialog}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{ 
            width: 48, 
            height: 48, 
            borderRadius: '50%', 
            bgcolor: '#3b82f6', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <PhoneIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Çağrı Sonucu
            </Typography>
            {dialerCall && (
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {dialerCall.campaignName} - {dialerCall.contactName || dialerCall.phoneNumber}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {dialerCall && (
            <Box>
              {/* Contact Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Telefon</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{dialerCall.phoneNumber}</Typography>
                  </Grid>
                  {dialerCall.contactName && (
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>İsim</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{dialerCall.contactName}</Typography>
                    </Grid>
                  )}
                  {dialerCall.contactCompany && (
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Firma</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{dialerCall.contactCompany}</Typography>
                    </Grid>
                  )}
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Deneme</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{dialerCall.attemptNumber}. arama</Typography>
                  </Grid>
                </Grid>
              </Box>
              
              {/* Disposition Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Sonuç Seçin *</InputLabel>
                <Select
                  value={selectedDisposition}
                  label="Sonuç Seçin *"
                  onChange={(e) => setSelectedDisposition(e.target.value)}
                >
                  <MenuItem value="answered">✅ Cevaplandı - Görüşme Yapıldı</MenuItem>
                  <MenuItem value="callback">📅 Geri Arama İstedi</MenuItem>
                  <MenuItem value="not_interested">❌ İlgilenmiyor</MenuItem>
                  <MenuItem value="wrong_number">📵 Yanlış Numara</MenuItem>
                  <MenuItem value="voicemail">📧 Sesli Mesaj</MenuItem>
                  <MenuItem value="busy">📞 Meşgul</MenuItem>
                  <MenuItem value="no_answer">📴 Cevap Yok</MenuItem>
                  <MenuItem value="dnc">🚫 Aranmasın (DNC)</MenuItem>
                  <MenuItem value="sale">💰 Satış Yapıldı</MenuItem>
                  <MenuItem value="appointment">📆 Randevu Alındı</MenuItem>
                </Select>
              </FormControl>
              
              {/* Notes */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notlar (Opsiyonel)"
                value={dispositionNotes}
                onChange={(e) => setDispositionNotes(e.target.value)}
                placeholder="Görüşme hakkında notlarınızı yazın..."
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowDispositionDialog(false);
              setSelectedDisposition('');
              setDispositionNotes('');
              setDialerCall(null);
            }}
            sx={{ 
              px: 4,
              borderColor: '#cbd5e1',
              color: '#64748b',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
            }}
          >
            Atla
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDisposition}
            disabled={!selectedDisposition || savingDisposition}
            startIcon={savingDisposition ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              px: 4,
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': { bgcolor: '#94a3b8' }
            }}
          >
            {savingDisposition ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AgentPanel;
